import React, { useState, useEffect, useRef, Suspense } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Schedule,
  LocationOn,
  Visibility,
  CheckCircle,
  Route,
  Dashboard,
  People,
  Delete,
  TrendingUp,
  HourglassEmpty,
  Close,
  SwapHoriz,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import SatelliteViewDialog from '../components/SatelliteViewDialog';
import DeepLawnMeasurementsDialog from '../components/DeepLawnMeasurementsDialog';
import ProviderMatchNotification from '../components/ProviderMatchNotification';
import RouteOptimizationDashboard from '../components/RouteOptimizationDashboard';
import CustomerManagement from '../components/CustomerManagement';
import ThisWeeksSchedule from '../components/ThisWeeksSchedule';
import TodaysJobs from '../components/TodaysJobs';
import IncompleteStopsResolver from '../components/IncompleteStopsResolver';
import AddToTodayDialog from '../components/AddToTodayDialog';
import AddAllToScheduleDialog from '../components/AddAllToScheduleDialog';
import MoveDayDialog from '../components/MoveDayDialog';
import CustomerScheduleDialog from '../components/CustomerScheduleDialog';
import DemoGuidedTour from '../components/DemoGuidedTour';
import InfoTip from '../components/InfoTip';
import { DemoDashboardSection } from '../config/demoTourSteps';
import { useScheduleData, ScheduleJob } from '../hooks/useScheduleData';
import { Bid } from '../types';
import ProviderPriceBreakdown from '../components/ProviderPriceBreakdown';
import ProviderBidDetailsDialog from '../components/ProviderBidDetailsDialog';
import { salesTaxOptionsForJob } from '../utils/salesTax';
import {
  getTodayInUserTimezone,
  getDayOfWeekInUserTimezone,
  formatDateForDisplay,
  formatContractDateUTC,
  isToday
} from '../utils/dateUtils';

const ProviderDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Helper function to normalize service names consistently
  // Maps both 'Lawn Mowing' and 'Edging/Trimming' to 'Lawn Mowing/Trimming'
  // Also handles variations like 'Lawn Mowing & Trimming/Edging'
  const normalizeServiceName = (name: string): string => {
    if (!name) return name;
    const normalized = name.trim();
    const lowerName = normalized.toLowerCase();
    
    // Check if this is a lawn mowing/edging/trimming service in any variation
    // Normalize all variations to 'Lawn Mowing/Trimming'
    if (lowerName.includes('mowing') || lowerName.includes('edging') || lowerName.includes('trimming') || lowerName.includes('trim')) {
      // Check if it's NOT fertilizing or weed control (those should stay as-is)
      if (!lowerName.includes('fertiliz') && !lowerName.includes('weed') && !lowerName.includes('snow')) {
        return 'Lawn Mowing/Trimming';
      }
    }
    
    // Check exact matches
    if (normalized === 'Lawn Mowing' || normalized === 'Edging/Trimming' || normalized === 'Edging' || 
        normalized === 'mowing' || normalized === 'edging' ||
        normalized === 'Lawn Mowing & Trimming' || normalized === 'Lawn Mowing & Trimming/Edging' ||
        normalized === 'Lawn Mowing/Edging' || normalized === 'Trimming/Edging' ||
        normalized === 'Lawn Mowing/Trim/Edge' || normalized === 'Lawn Mowing/Trimming') {
      return 'Lawn Mowing/Trimming';
    }
    
    return normalized;
  };

  // Helper function to format service type names (from serviceType.name string)
  const formatServiceTypeName = (serviceTypeName: string | undefined): string => {
    if (!serviceTypeName) return 'Service';
    return serviceTypeName
      .split(', ')
      .map((s: string) => {
        // Normalize service name to check for mowing variations
        const normalized = s.trim();
        if (normalized === 'mowing' || normalized === 'edging' || 
            normalized.toLowerCase().includes('mowing') || 
            normalized.toLowerCase().includes('trim') || 
            normalized.toLowerCase().includes('edge')) {
          return 'Lawn Mowing/Trimming';
        }
        if (s === 'fertilizingWeedControl' || normalized.toLowerCase().includes('fertilizing') || normalized.toLowerCase().includes('weed')) {
          return 'Weed Control/Fertilizing';
        }
        return s.charAt(0).toUpperCase() + s.slice(1);
      })
      .filter((name: string, index: number, arr: string[]) => arr.indexOf(name) === index) // Remove duplicates
      .join(', ');
  };
  const [requests, setRequests] = useState<any[]>([]);
  const [bids, setBids] = useState<Bid[]>([]);
  const [acceptedJobs, setAcceptedJobs] = useState<any[]>([]);
  const [paymentStatuses, setPaymentStatuses] = useState<Map<string, any>>(new Map());
  // Track dismissed per-instance notifications (stored in localStorage)
  const [dismissedPerInstanceNotifications, setDismissedPerInstanceNotifications] = useState<Set<string>>(() => {
    // Load from localStorage on mount
    const stored = localStorage.getItem('dismissedPerInstanceNotifications');
    return stored ? new Set(JSON.parse(stored)) : new Set<string>();
  });

  // Helper function to check if a job is per-instance
  const isPerInstanceJob = (job: any): boolean => {
    // Snow Removal services can always be scheduled later (weather-dependent)
    if (job.serviceType?.category === 'snow_removal') {
      return true;
    }
    // Check if the job frequency is per-instance
    if (job.frequency === 'per-instance') {
      console.log('[IS-PER-INSTANCE] Job is per-instance (direct frequency):', job._id);
      return true;
    }
    
    // Check accepted bid's servicePricing for per-instance frequency
    if (job.acceptedBid?.servicePricing && Array.isArray(job.acceptedBid.servicePricing)) {
      const serviceCategory = job.serviceType?.category;
      console.log('[IS-PER-INSTANCE] Checking job:', {
        jobId: job._id,
        serviceCategory,
        hasAcceptedBid: !!job.acceptedBid,
        servicePricingCount: job.acceptedBid.servicePricing.length,
        servicePricing: job.acceptedBid.servicePricing.map((sp: any) => ({
          service: sp.service,
          serviceName: sp.serviceName,
          frequency: sp.frequency,
          seasonalContractDetails: sp.seasonalContractDetails
        }))
      });
      
      let matchingServiceKey: string | null = null;
      if (serviceCategory === 'mowing') {
        matchingServiceKey = 'lawnMowingTrimming';
      } else if (serviceCategory === 'fertilizing') {
        matchingServiceKey = 'weedControlFertilizing';
      } else if (serviceCategory === 'snow_removal') {
        matchingServiceKey = 'snowRemoval';
      }
      
      if (matchingServiceKey) {
        // Normalize serviceCategory for matching (remove underscores, convert to lowercase)
        const normalizedCategory = (serviceCategory || '').replace(/_/g, '').toLowerCase();
        
        const matchingService = job.acceptedBid.servicePricing.find((sp: any) => {
          // Check exact match on service key
          if (sp.service === matchingServiceKey) return true;
          
          // Check if service name matches (case-insensitive, handle spaces/underscores)
          const normalizedService = (sp.service || '').toLowerCase().replace(/\s+/g, '');
          const normalizedServiceName = (sp.serviceName || '').toLowerCase().replace(/\s+/g, '');
          
          // Match if service/serviceName contains the category keywords
          if (normalizedService.includes(normalizedCategory) || normalizedServiceName.includes(normalizedCategory)) {
            return true;
          }
          
          // Also check if service/serviceName contains key words from the category
          if (normalizedCategory.includes('snow') && (normalizedService.includes('snow') || normalizedServiceName.includes('snow'))) {
            return true;
          }
          if (normalizedCategory.includes('mowing') && (normalizedService.includes('mowing') || normalizedService.includes('mow') || normalizedServiceName.includes('mowing') || normalizedServiceName.includes('mow'))) {
            return true;
          }
          if (normalizedCategory.includes('fertilizing') && (normalizedService.includes('fertiliz') || normalizedService.includes('weed') || normalizedServiceName.includes('fertiliz') || normalizedServiceName.includes('weed'))) {
            return true;
          }
          
          return false;
        });
        
        console.log('[IS-PER-INSTANCE] Matching service found:', {
          jobId: job._id,
          matchingServiceKey,
          normalizedCategory,
          matchingService: matchingService ? {
            service: matchingService.service,
            serviceName: matchingService.serviceName,
            frequency: matchingService.frequency,
            seasonalContractDetails: matchingService.seasonalContractDetails
          } : null
        });
        
        if (matchingService) {
          // Check if it's a seasonal contract with per-instance serviceFrequency
          if (matchingService.frequency === 'seasonal-contract' && matchingService.seasonalContractDetails?.serviceFrequency === 'per-instance') {
            console.log('[IS-PER-INSTANCE] Job is per-instance (seasonal contract with per-instance serviceFrequency):', job._id);
            return true;
          }
          // Check if frequency is directly per-instance
          if (matchingService.frequency === 'per-instance') {
            console.log('[IS-PER-INSTANCE] Job is per-instance (servicePricing frequency):', job._id);
            return true;
          }
        }
      }
    } else {
      console.log('[IS-PER-INSTANCE] Job does not have acceptedBid.servicePricing:', {
        jobId: job._id,
        hasAcceptedBid: !!job.acceptedBid,
        hasServicePricing: !!job.acceptedBid?.servicePricing
      });
    }
    
    return false;
  };

  // Function to dismiss per-instance notification
  const dismissPerInstanceNotification = (jobId: string) => {
    const newDismissed = new Set(dismissedPerInstanceNotifications);
    newDismissed.add(jobId);
    setDismissedPerInstanceNotifications(newDismissed);
    // Save to localStorage
    localStorage.setItem('dismissedPerInstanceNotifications', JSON.stringify(Array.from(newDismissed)));
  };
  
  // Use shared schedule data hook
  const {
    scheduledJobs,
    todaysJobs,
    loading: scheduleDataLoading,
    error: scheduleDataError,
    refreshSchedules,
    updateJobOptimistically,
    updateJobFromResponse,
    addJobOptimistically,
  } = useScheduleData();
  
  // Track last schedule update time to prevent periodic refresh right after update
  const lastScheduleUpdateRef = useRef<number>(0);
  const [retryingPayment, setRetryingPayment] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [providerRating, setProviderRating] = useState<{ rating: number; totalReviews: number } | null>(null);
  const [dismissedCurrentCustomerNotification, setDismissedCurrentCustomerNotification] = useState(false);
  const [bidDialog, setBidDialog] = useState({ open: false, requestId: '', requestData: null as any });
  const [bidDetailsDialog, setBidDetailsDialog] = useState({ open: false, bidId: null as string | null });
  const [isCustomerOnboardedByThisProvider, setIsCustomerOnboardedByThisProvider] = useState(false);
  const [bidForm, setBidForm] = useState({ 
    description: '', 
    servicePricing: [] as Array<{ 
      service: string, 
      serviceName: string,
      price: string, 
      duration: string,
      frequency: string,
      isSeasonalContract: boolean,
      seasonalContractDetails?: {
        contractStartDate: string,
        contractEndDate: string,
        totalContractPrice: string,
        serviceFrequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance',
        paymentSchedule?: 'single-payment' | 'monthly' // Payment schedule (defaults to single-payment)
      }
    }>
  });
  const [bidError, setBidError] = useState('');
  const [bidFieldErrors, setBidFieldErrors] = useState<Record<string, string>>({});
  const [bidLoading, setBidLoading] = useState(false);
  const [satelliteDialog, setSatelliteDialog] = useState({ open: false, address: '' });
  const [measurementsDialog, setMeasurementsDialog] = useState<{ open: boolean; serviceRequestId: string; propertyType: string; address: string; initialData: any }>({ open: false, serviceRequestId: '', propertyType: '', address: '', initialData: null });
  const [requestDetailsDialog, setRequestDetailsDialog] = useState({ open: false, request: null as any });
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, job: null as any });
  const [scheduleForm, setScheduleForm] = useState({ scheduledDate: '', estimatedDuration: '' });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [completeDialog, setCompleteDialog] = useState({ open: false, job: null as any, completionNotes: '' });
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [payments, setPayments] = useState<any[]>([]);
  const [activeSection, setActiveSection] = useState<DemoDashboardSection>('dashboard');
  const [editScheduleDialog, setEditScheduleDialog] = useState({ open: false, job: null as any, newDate: '', isDayChange: false });
  const [dayResetDialog, setDayResetDialog] = useState({ open: false, job: null as any, newDate: '', resetSchedule: false });
  const [scheduleUpdateSuccess, setScheduleUpdateSuccess] = useState<string | null>(null);
  const [scheduleUpdateError, setScheduleUpdateError] = useState<string | null>(null);
  const [scheduleUpdateLoading, setScheduleUpdateLoading] = useState(false);
  const [onboardDialogOpen, setOnboardDialogOpen] = useState(false);
  const [customerManagementRefreshTrigger, setCustomerManagementRefreshTrigger] = useState(0);
  const [addToScheduleDialogOpen, setAddToScheduleDialogOpen] = useState(false);
  const [moveDayDialogOpen, setMoveDayDialogOpen] = useState(false);
  const [addAllDialogOpen, setAddAllDialogOpen] = useState(false);
  const [customerScheduleDialogOpen, setCustomerScheduleDialogOpen] = useState(false);
  const [customerScheduleDefaultDate, setCustomerScheduleDefaultDate] = useState<string | undefined>(undefined);
  const [earningsData, setEarningsData] = useState<any>(null);
  const [earningsLoading, setEarningsLoading] = useState(false);

  const fetchProviderRating = async () => {
    // Use user's rating directly - no API call needed since we removed quick stats
    // This prevents blocking login/dashboard load
    const provider = user as any;
    if (provider?.rating !== undefined) {
      setProviderRating({
        rating: provider.rating || 0,
        totalReviews: provider.totalReviews || provider.totalJobs || 0
      });
    } else {
      // Fallback if rating not available
      setProviderRating({
        rating: 0,
        totalReviews: 0
      });
    }
  };

  useEffect(() => {
    // All API calls are non-blocking - failures won't prevent dashboard from loading
    // Critical data - load immediately (but with error handling)
    Promise.allSettled([
      Promise.resolve(fetchAvailableRequests()).catch(err => console.error('Failed to fetch requests:', err)),
      Promise.resolve(fetchMyBids()).catch(err => console.error('Failed to fetch bids:', err)),
      Promise.resolve(fetchAcceptedJobs()).catch(err => console.error('Failed to fetch accepted jobs:', err)),
      Promise.resolve(refreshSchedules()).catch(err => console.error('Failed to refresh schedules:', err)),
      Promise.resolve(checkForNewAcceptedBids()).catch(err => console.error('Failed to check new bids:', err)),
      Promise.resolve(fetchNotifications()).catch(err => console.error('Failed to fetch notifications:', err)),
    ]);
    
    // Non-critical data - load after a delay to prevent blocking login
    fetchProviderRating(); // Uses user.rating directly - no API call
    setTimeout(() => {
      fetchPayments().catch(err => console.error('Failed to fetch payments:', err));
      fetchEarningsData().catch(err => console.error('Failed to fetch earnings:', err));
    }, 1000);

    // Refresh data when the page regains focus (e.g., user switches back from another tab)
    const handleFocus = () => {
      fetchAvailableRequests();
      fetchMyBids();
      fetchAcceptedJobs();
      refreshSchedules(); // Use shared hook
      checkForNewAcceptedBids();
      fetchPayments();
      fetchNotifications();
      fetchProviderRating(); // Refresh rating
    };

    // Refresh data periodically (every 30 seconds) to catch real-time changes
    // Skip schedule refresh if we just updated a schedule (within last 30 seconds)
    // This prevents the 10-15 second delay where jobs disappear during refresh
    const refreshInterval = setInterval(() => {
      fetchAvailableRequests();
      fetchMyBids();
      fetchAcceptedJobs();
      
      // Only refresh schedules if it's been more than 30 seconds since last update
      // This prevents refresh right after updateJobFromResponse updates the state
      const timeSinceLastUpdate = Date.now() - lastScheduleUpdateRef.current;
      if (timeSinceLastUpdate > 30000) {
        refreshSchedules(); // Use shared hook
      } else {
        console.log('[PERIODIC-REFRESH] Skipping schedule refresh - recent update detected', {
          timeSinceLastUpdate: Math.round(timeSinceLastUpdate / 1000) + 's',
          threshold: '30s'
        });
      }
      
      checkForNewAcceptedBids();
      fetchPayments();
      fetchNotifications();
      fetchProviderRating(); // Refresh rating
      fetchEarningsData(); // Refresh earnings data
    }, 30000);

    window.addEventListener('focus', handleFocus);
    
    // Listen for schedule update events from CustomerManagement
    // NOTE: We don't refresh schedules here anymore because updateJobFromResponse
    // already updates the state with authoritative data from POST responses.
    // This prevents the 10-15 second delay where jobs disappear during refresh.
    const handleScheduleUpdate = async () => {
      console.log('[SCHEDULE-UPDATE] Event received - skipping refresh (state already updated via updateJobFromResponse)');
      // Only refresh accepted jobs, not schedules (schedules are updated via updateJobFromResponse)
      fetchAcceptedJobs();
      console.log('[SCHEDULE-UPDATE] Accepted jobs refreshed');
    };
    
    window.addEventListener('customer-schedule-updated', handleScheduleUpdate);
    window.addEventListener('refresh-todays-schedule', handleScheduleUpdate);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('customer-schedule-updated', handleScheduleUpdate);
      window.removeEventListener('refresh-todays-schedule', handleScheduleUpdate);
      clearInterval(refreshInterval);
    };
  }, [refreshSchedules]);

  // Note: Today's Jobs filtering is now handled by the useScheduleData hook

  // Fetch payment statuses for completed jobs
  // CRITICAL: Debounce to avoid flickering during schedule updates
  // Preserve existing payment statuses during refresh to prevent "Retry Payment" from flashing
  useEffect(() => {
    // Small delay to allow schedule refresh to complete first
    const timeoutId = setTimeout(() => {
      const fetchPaymentStatuses = async () => {
        // CRITICAL: Include jobs that were completed today, even if status is now 'scheduled' (due to auto-rescheduling)
        // When backend auto-reschedules completed services, status becomes 'scheduled' but completedDate remains set to today
        // We need to fetch payment statuses for these services too, not just services with status === 'completed'
        const completedJobIds = todaysJobs
          .filter(job => {
            // Include if status is 'completed' OR if completedDate is today (auto-rescheduled completed service)
            const isCompletedStatus = job.status === 'completed';
            const completedDate = (job as any).completedDate;
            const wasCompletedToday = completedDate && isToday(completedDate);
            return isCompletedStatus || wasCompletedToday;
          })
          .map(job => job._id?.toString())
          .filter(Boolean);

        if (completedJobIds.length === 0) {
          // Only clear payment statuses if there are no completed jobs
          // Don't clear if we're just refreshing and jobs still exist
          const hasCompletedJobs = todaysJobs.some(job => {
            const isCompletedStatus = job.status === 'completed';
            const completedDate = (job as any).completedDate;
            const wasCompletedToday = completedDate && isToday(completedDate);
            return isCompletedStatus || wasCompletedToday;
          });
          if (!hasCompletedJobs) {
            setPaymentStatuses(new Map());
          }
          return;
        }

        try {
          // CRITICAL: Preserve existing payment statuses to prevent flickering
          // Use functional update to access current state without adding it to dependencies
          setPaymentStatuses((existingStatuses) => {
            // Fetch payment records for completed jobs
            const paymentPromises = completedJobIds.map(async (jobId) => {
              try {
                const response = await apiService.get(`/payments/job/${jobId}`) as any;
                if (response.success && response.data) {
                  return { jobId, payment: response.data };
                }
              } catch (err: any) {
                // For network/CORS errors during refresh, preserve existing status if available
                if (err.isNetworkError || err.code === 'ERR_NETWORK' || err.message?.includes('CORS')) {
                  const existingPayment = existingStatuses.get(jobId);
                  if (existingPayment) {
                    console.log(`[PAYMENT-STATUSES] Network error for job ${jobId}, preserving existing status:`, existingPayment.status);
                    return { jobId, payment: existingPayment };
                  }
                }
                // Payment might not exist yet, that's okay
                if (err.status !== 404) {
                  console.error(`Failed to fetch payment for job ${jobId}:`, err);
                }
              }
              return { jobId, payment: null };
            });

            // Wait for all promises to resolve, then update state
            Promise.all(paymentPromises).then((results) => {
              setPaymentStatuses((currentStatuses) => {
                const statusMap = new Map<string, any>(currentStatuses); // Start with current statuses
                results.forEach(({ jobId, payment }) => {
                  if (jobId) {
                    // Only update if we got a valid payment response
                    // This prevents clearing valid statuses during network errors
                    if (payment !== null) {
                      statusMap.set(jobId, payment);
                    } else if (!currentStatuses.has(jobId)) {
                      // Only set to null if we don't have an existing status
                      // This prevents clearing valid statuses during temporary network errors
                      statusMap.set(jobId, null);
                    }
                  }
                });
                console.log('[PAYMENT-STATUSES] Fetched payment statuses:', {
                  total: statusMap.size,
                  updated: results.filter(r => r.payment !== null).length,
                  preserved: Array.from(currentStatuses.keys()).filter(k => statusMap.has(k) && statusMap.get(k) === currentStatuses.get(k)).length,
                  statuses: Array.from(statusMap.entries()).map(([jobId, payment]) => ({
                    jobId,
                    paymentStatus: payment?.status || 'no payment record',
                    amount: payment?.amount,
                    failureReason: payment?.failureReason
                  }))
                });
                return statusMap;
              });
            }).catch((err) => {
              console.error('Failed to fetch payment statuses:', err);
              // On error, don't update state - existing statuses are preserved
            });
            
            // Return existing statuses immediately to prevent flickering
            return existingStatuses;
          });
        } catch (err) {
          console.error('Failed to fetch payment statuses:', err);
          // On error, preserve existing statuses to prevent flickering
          // Don't clear paymentStatuses on error
        }
      };

      if (todaysJobs.length > 0) {
        fetchPaymentStatuses();
      }
    }, 300); // 300ms delay to allow schedule refresh to complete

    return () => clearTimeout(timeoutId);
  }, [todaysJobs]);

  // Fetch full service request when bid dialog opens so we get customerAttachments with viewUrl
  useEffect(() => {
    if (!bidDialog.open || !bidDialog.requestId) return;
    let cancelled = false;
    apiService.get(`/service-requests/${bidDialog.requestId}`).then((res: any) => {
      if (cancelled || !res?.data?.serviceRequest) return;
      setBidDialog(prev => ({ ...prev, requestData: res.data.serviceRequest }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [bidDialog.open, bidDialog.requestId]);

  // Fetch full service request when request details dialog opens so we get customerAttachments with viewUrl
  useEffect(() => {
    const id = requestDetailsDialog.request?._id;
    if (!requestDetailsDialog.open || !id) return;
    let cancelled = false;
    apiService.get(`/service-requests/${id}`).then((res: any) => {
      if (cancelled || !res?.data?.serviceRequest) return;
      setRequestDetailsDialog(prev => ({ ...prev, request: res.data.serviceRequest }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [requestDetailsDialog.open, requestDetailsDialog.request?._id]);

  const fetchAvailableRequests = async () => {
    try {
      // Fetch both pending and bidding requests
      const [pendingResponse, biddingResponse] = await Promise.all([
        apiService.get('/service-requests?userType=provider&status=pending'),
        apiService.get('/service-requests?userType=provider&status=bidding')
      ]);
      
      const pendingRequests = pendingResponse.success ? (pendingResponse.data as any).serviceRequests || [] : [];
      const biddingRequests = biddingResponse.success ? (biddingResponse.data as any).serviceRequests || [] : [];
      
      // Deduplicate by _id in case the same request appears in both lists
      const requestMap = new Map();
      [...pendingRequests, ...biddingRequests].forEach((request: any) => {
        if (request._id) {
          requestMap.set(request._id, request);
        }
      });
      
      const allRequests = Array.from(requestMap.values());
      console.log(`[CLIENT] Total requests after deduplication: ${allRequests.length}`);
      
      // Sort requests: current customers first, then others
      const sortedRequests = allRequests.sort((a: any, b: any) => {
        if (a.isCurrentCustomer && !b.isCurrentCustomer) return -1;
        if (!a.isCurrentCustomer && b.isCurrentCustomer) return 1;
        return 0;
      });
      
      setRequests(sortedRequests);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBids = async () => {
    try {
      const response = await apiService.get('/bids?userType=provider');
      if (response.success && response.data) {
        setBids((response.data as any).bids || []);
      }
    } catch (err) {
      console.error('Failed to fetch bids:', err);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await apiService.get('/notifications?limit=5');
      if (response.success && response.data) {
        const data = response.data as { notifications: any[]; unreadCount: number };
        setNotifications(data.notifications || []);
        setUnreadNotificationCount(data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const fetchAcceptedJobs = async () => {
    try {
      const response = await apiService.get('/service-requests?userType=provider&status=accepted');
      if (response.success && response.data) {
        setAcceptedJobs((response.data as any).serviceRequests || []);
      }
    } catch (err) {
      console.error('Failed to fetch accepted jobs:', err);
    }
  };

  // Note: fetchScheduledJobs and fetchTodaysJobs are now handled by the useScheduleData hook

  const fetchPayments = async () => {
    try {
      const response = await apiService.get('/service-requests/provider/payments');
      if (response.success && response.data) {
        const allPayments = (response.data as any).payments || [];
        
        // Filter out monthly payment seasonal contract skip payments
        // These are payments with status='skip' and type='seasonal_contract_subscription_skip'
        // They represent service completions where subscription is active but no charge was made this time
        // Only show them if it's the 1st of the month (when monthly payment would have been processed)
        const today = new Date();
        const isFirstOfMonth = today.getDate() === 1;
        
        const filteredPayments = allPayments.filter((payment: any) => {
          const isMonthlySkip = payment.status === 'skip' && payment.type === 'seasonal_contract_subscription_skip';
          if (isMonthlySkip) {
            // Only include if it's the 1st of the month (monthly payment would have been processed)
            // Otherwise, exclude it since no payment was actually made
            if (isFirstOfMonth) {
              return true; // Keep it - monthly payment was processed today
            } else {
              return false; // Exclude it - no payment made, just a skip record
            }
          }
          return true; // Keep all other payments
        });
        
        setPayments(filteredPayments);
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    }
  };

  const fetchEarningsData = async () => {
    try {
      setEarningsLoading(true);
      const response = await apiService.get('/providers/earnings/status');
      if (response.success && response.data) {
        setEarningsData(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch earnings data:', err);
    } finally {
      setEarningsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatEarningsDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const checkForNewAcceptedBids = async () => {
    try {
      // Get accepted bids that might be new
      const response = await apiService.get('/service-requests?userType=provider&status=accepted');
      if (response.success && response.data) {
        const currentAcceptedJobs = (response.data as any).serviceRequests || [];
        
        // Check if there are new accepted jobs that weren't in our previous state
        const newJobs = currentAcceptedJobs.filter((job: any) => {
          return !acceptedJobs.some(existingJob => existingJob._id === job._id);
        });

        // Create notifications for new accepted jobs
        if (newJobs.length > 0) {
          const newNotifications = newJobs.map((job: any) => ({
            id: Date.now() + Math.random(),
            type: 'bid_accepted',
            title: 'Bid Accepted!',
            message: `Your bid for ${job.serviceType?.name} at ${job.address?.street} has been accepted. Please schedule the job.`,
            job: job,
            timestamp: new Date(),
            read: false,
          }));

          setNotifications(prev => [...newNotifications, ...prev]);
        }
      }
    } catch (err) {
      console.error('Failed to check for new accepted bids:', err);
    }
  };

  const markNotificationAsRead = (notificationId: any) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const clearNotification = (notificationId: any) => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'bidding':
        return 'info';
      case 'accepted':
        return 'success';
      case 'scheduled':
        return 'primary';
      case 'in-progress':
        return 'secondary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending_cancellation':
        return 'warning'; // Warning color for pending cancellation (yellow/orange)
      case 'expired':
        return 'error'; // Expired bids - red/error color
      case 'expired_unaccepted':
        return 'error'; // Expired service requests - red/error color
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date | string) => {
    return formatDateForDisplay(date);
    return new Date(date).toLocaleDateString();
  };

  const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const formatPropertyType = (propertyType: string) => {
    if (!propertyType) return 'N/A';
    
    const typeMap: { [key: string]: string } = {
      'single_residential': 'Single Family',
      'multi_family_condo': 'Multi-Family/Condo',
      'commercial': 'Commercial',
      'vacant_lot': 'Vacant Lot',
      'hoa_managed': 'HOA-Managed',
    };
    
    return typeMap[propertyType] || propertyType;
  };

  const handleBidSubmit = async (requestId: string) => {
    console.log('[BID-SUBMIT] Starting bid submit for request:', requestId);
    const requestData = requests.find(r => r._id === requestId);
    
    // Reset state first
    setIsCustomerOnboardedByThisProvider(false);
    console.log('[BID-SUBMIT] Reset onboarding status to false');
    console.log('[BID-SUBMIT] Request data:', {
      hasCustomerId: !!requestData?.customerId,
      isOnboardedCustomer: requestData?.isOnboardedCustomer,
      requestId
    });
    
    // Check if THIS provider has onboarded this customer
    // Fee percentage: 1% for onboarded, 5% for marketplace
    // IMPORTANT: We cannot use requestData.isOnboardedCustomer flag because it indicates if ANY provider
    // has onboarded the customer, not THIS specific provider. We must check THIS provider's ExistingCustomer records.
    let isOnboarded = false;
    
    // Check if THIS provider has onboarded this customer by checking their ExistingCustomer records
    if (requestData?.customerId) {
      // Second check: If customerId is available, check against existing customers
      try {
        // Extract customerId - handle both populated object and ObjectId string
        const customerId = typeof requestData.customerId === 'object' 
          ? (requestData.customerId as any)?._id || (requestData.customerId as any)?.id 
          : requestData.customerId;
        
        // Extract email/phone if customerId is populated (may not be available for pending/bidding requests)
        const customerEmail = typeof requestData.customerId === 'object' && (requestData.customerId as any)?.email
          ? (requestData.customerId as any).email
          : null;
        const customerPhone = typeof requestData.customerId === 'object' && (requestData.customerId as any)?.phone
          ? (requestData.customerId as any).phone
          : null;
        
        if (customerId) {
          // Fetch this provider's existing customers to check if this customer is onboarded
          const existingCustomersResponse = await apiService.get('/route-optimization/existing-customers');
          if (existingCustomersResponse.success && Array.isArray(existingCustomersResponse.data)) {
            const existingCustomers = existingCustomersResponse.data as any[];
            
            // Check if this customer is in this provider's onboarded customers list
            // Match by linkedCustomerId, email, or phone (any property address)
            // CRITICAL: Only count as onboarded if customerSource === 'onboarded'
            // Bid-accepted customers (customerSource === 'bid') are new platform customers and should get 5% fee
            // This matches the backend Bid model logic exactly
            const customerOnboarded = existingCustomers.some((ec: any) => {
              // First check: Only onboarded customers (customerSource === 'onboarded') get 1% fee
              // Bid-accepted customers (customerSource === 'bid') are new platform customers and should get 5% fee
              const isOnboarded = ec.customerSource === 'onboarded' || 
                                  (ec.customerSource !== 'bid' && ec.linkedAt);
              
              if (!isOnboarded) {
                return false; // Not an onboarded customer, skip
              }
              
              // Check by linkedCustomerId (primary check)
              const ecLinkedCustomerId = typeof ec.linkedCustomerId === 'object'
                ? (ec.linkedCustomerId as any)?._id || (ec.linkedCustomerId as any)?.id
                : ec.linkedCustomerId;
              
              if (ecLinkedCustomerId && customerId && ecLinkedCustomerId.toString() === customerId.toString()) {
                return true;
              }
              
              // Check by email (case-insensitive) - only if email is available
              if (customerEmail && ec.email) {
                if (ec.email.toLowerCase() === customerEmail.toLowerCase()) {
                  return true;
                }
              }
              
              // Check by phone - only if phone is available
              if (customerPhone && ec.phone) {
                if (ec.phone === customerPhone) {
                  return true;
                }
              }
              
              return false;
            });
            
            isOnboarded = customerOnboarded;
            
            // Detailed logging for debugging
            const existingCustomersWithLinkedId = existingCustomers
              .map((ec: any) => {
                const ecLinkedCustomerId = typeof ec.linkedCustomerId === 'object'
                  ? (ec.linkedCustomerId as any)?._id || (ec.linkedCustomerId as any)?.id
                  : ec.linkedCustomerId;
                return {
                  existingCustomerId: ec._id?.toString(),
                  linkedCustomerId: ecLinkedCustomerId?.toString(),
                  email: ec.email,
                  phone: ec.phone,
                  firstName: ec.firstName,
                  lastName: ec.lastName,
                  isActive: ec.isActive,
                  customerSource: ec.customerSource || 'N/A',
                  linkedAt: ec.linkedAt ? new Date(ec.linkedAt).toISOString() : 'N/A'
                };
              })
              .filter((ec: any) => !!ec.linkedCustomerId);
            
            console.log('[BID-SUBMIT] Customer onboarding check:', {
              requestId: requestId,
              customerId: customerId?.toString(),
              customerIdType: typeof customerId,
              customerEmail: customerEmail || 'not available',
              customerPhone: customerPhone || 'not available',
              isOnboarded,
              existingCustomersCount: existingCustomers.length,
              existingCustomersWithLinkedIdCount: existingCustomersWithLinkedId.length,
              existingCustomersWithLinkedId: existingCustomersWithLinkedId.map((ec: any) => ({
                ...ec,
                customerSource: ec.customerSource || 'N/A'
              })),
              matchDetails: existingCustomersWithLinkedId.map((ec: any) => {
                const ecLinkedCustomerId = ec.linkedCustomerId; // Already converted to string in mapping
                const matchesById = ecLinkedCustomerId && customerId && ecLinkedCustomerId === customerId.toString();
                const matchesByEmail = customerEmail && ec.email && ec.email.toLowerCase() === customerEmail.toLowerCase();
                const matchesByPhone = customerPhone && ec.phone && ec.phone === customerPhone;
                const isOnboardedCheck = ec.customerSource === 'onboarded' || 
                                        (ec.customerSource !== 'bid' && ec.linkedAt);
                return {
                  ...ec,
                  matchesById,
                  matchesByEmail,
                  matchesByPhone,
                  isOnboardedCheck,
                  wouldMatch: (matchesById || matchesByEmail || matchesByPhone) && isOnboardedCheck
                };
              })
            });
          }
        }
      } catch (err) {
        console.error('Error checking if customer is onboarded:', err);
        // Default to false (5% fee) if check fails
        isOnboarded = false;
      }
    } else {
      // Third check: If no customerId and no flag, fetch full service request details
      console.log('[BID-SUBMIT] No customerId or flag, fetching full service request details');
      try {
        const fullRequestResponse = await apiService.get(`/service-requests/${requestId}`) as any;
        console.log('[BID-SUBMIT] Full request response:', {
          success: fullRequestResponse.success,
          hasServiceRequest: !!fullRequestResponse.data?.serviceRequest,
          customerIdType: typeof fullRequestResponse.data?.serviceRequest?.customerId,
          customerId: fullRequestResponse.data?.serviceRequest?.customerId
        });
        
        if (fullRequestResponse.success && fullRequestResponse.data?.serviceRequest) {
          const fullRequest = fullRequestResponse.data.serviceRequest;
          const customerId = typeof fullRequest.customerId === 'object' 
            ? (fullRequest.customerId as any)?._id || (fullRequest.customerId as any)?.id 
            : fullRequest.customerId;
          
          console.log('[BID-SUBMIT] Extracted customerId:', customerId?.toString() || 'NOT FOUND');
          
          if (customerId) {
            // Fetch this provider's existing customers to check if this customer is onboarded
            const existingCustomersResponse = await apiService.get('/route-optimization/existing-customers');
            if (existingCustomersResponse.success && Array.isArray(existingCustomersResponse.data)) {
              const existingCustomers = existingCustomersResponse.data as any[];
              
              // Check if this customer is in this provider's onboarded customers list
              // CRITICAL: Only count as onboarded if customerSource === 'onboarded'
              // Bid-accepted customers (customerSource === 'bid') are new platform customers and should get 5% fee
              const customerOnboarded = existingCustomers.some((ec: any) => {
                // First check: Only onboarded customers (customerSource === 'onboarded') get 1% fee
                // Bid-accepted customers (customerSource === 'bid') are new platform customers and should get 5% fee
                const isOnboarded = ec.customerSource === 'onboarded' || 
                                    (ec.customerSource !== 'bid' && ec.linkedAt);
                
                if (!isOnboarded) {
                  return false; // Not an onboarded customer, skip
                }
                
                const ecLinkedCustomerId = typeof ec.linkedCustomerId === 'object'
                  ? (ec.linkedCustomerId as any)?._id || (ec.linkedCustomerId as any)?.id
                  : ec.linkedCustomerId;
                
                if (ecLinkedCustomerId && customerId && ecLinkedCustomerId.toString() === customerId.toString()) {
                  return true;
                }
                
                // Check by email if available
                const customerEmail = typeof fullRequest.customerId === 'object' && (fullRequest.customerId as any)?.email
                  ? (fullRequest.customerId as any).email
                  : null;
                if (customerEmail && ec.email && ec.email.toLowerCase() === customerEmail.toLowerCase()) {
                  return true;
                }
                
                // Check by phone if available
                const customerPhone = typeof fullRequest.customerId === 'object' && (fullRequest.customerId as any)?.phone
                  ? (fullRequest.customerId as any).phone
                  : null;
                if (customerPhone && ec.phone && ec.phone === customerPhone) {
                  return true;
                }
                
                return false;
              });
              
              isOnboarded = customerOnboarded;
              
              // Detailed logging for debugging
              const existingCustomersWithLinkedId = existingCustomers
                .map((ec: any) => {
                  const ecLinkedCustomerId = typeof ec.linkedCustomerId === 'object'
                    ? (ec.linkedCustomerId as any)?._id || (ec.linkedCustomerId as any)?.id
                    : ec.linkedCustomerId;
                  return {
                    existingCustomerId: ec._id?.toString(),
                    linkedCustomerId: ecLinkedCustomerId?.toString(),
                    email: ec.email,
                    phone: ec.phone,
                    firstName: ec.firstName,
                    lastName: ec.lastName,
                    isActive: ec.isActive,
                    customerSource: ec.customerSource || 'N/A',
                    linkedAt: ec.linkedAt ? new Date(ec.linkedAt).toISOString() : 'N/A'
                  };
                })
                .filter((ec: any) => !!ec.linkedCustomerId);
              
              const customerEmail = typeof fullRequest.customerId === 'object' && (fullRequest.customerId as any)?.email
                ? (fullRequest.customerId as any).email
                : null;
              const customerPhone = typeof fullRequest.customerId === 'object' && (fullRequest.customerId as any)?.phone
                ? (fullRequest.customerId as any).phone
                : null;
              
              console.log('[BID-SUBMIT] Fetched full request, onboarding check result:', {
                customerId: customerId?.toString(),
                customerEmail: customerEmail || 'not available',
                customerPhone: customerPhone || 'not available',
                isOnboarded,
                existingCustomersCount: existingCustomers.length,
                existingCustomersWithLinkedIdCount: existingCustomersWithLinkedId.length,
                existingCustomersWithLinkedId: existingCustomersWithLinkedId,
                matchDetails: existingCustomersWithLinkedId.map((ec: any) => {
                  const ecLinkedCustomerId = ec.linkedCustomerId;
                  const matchesById = ecLinkedCustomerId && customerId && ecLinkedCustomerId === customerId.toString();
                  const matchesByEmail = customerEmail && ec.email && ec.email.toLowerCase() === customerEmail.toLowerCase();
                  const matchesByPhone = customerPhone && ec.phone && ec.phone === customerPhone;
                  const isOnboardedCheck = ec.customerSource === 'onboarded' || 
                                          (ec.customerSource !== 'bid' && ec.linkedAt !== 'N/A');
                  return {
                    ...ec,
                    matchesById,
                    matchesByEmail,
                    matchesByPhone,
                    isOnboardedCheck,
                    wouldMatch: (matchesById || matchesByEmail || matchesByPhone) && isOnboardedCheck
                  };
                })
              });
            } else {
              console.log('[BID-SUBMIT] No customerId found in full request, cannot check onboarding status');
            }
          } else {
            console.log('[BID-SUBMIT] Full request response missing serviceRequest data');
          }
        } else {
          console.log('[BID-SUBMIT] Full request fetch failed or returned no data');
        }
      } catch (err) {
        console.error('[BID-SUBMIT] Error fetching full service request:', err);
        // Default to false (5% fee) if check fails
        isOnboarded = false;
      }
    }
    
    // Set the onboarding status BEFORE opening the dialog
    setIsCustomerOnboardedByThisProvider(isOnboarded);
    console.log('[BID-SUBMIT] Final onboarding status:', isOnboarded, 'for request:', requestId);
    
    // Parse services from the request
    const services = requestData?.serviceType?.name?.split(', ') || [];
    const servicePricing = services.map((service: string) => {
      const serviceName = (service === 'mowing' || service === 'edging') ? 'Lawn Mowing/Trimming' :
                         service === 'fertilizingWeedControl' ? 'Fertilizing/Weed Control' :
                         service === 'snowRemoval' ? 'Snow Removal' :
                         service.charAt(0).toUpperCase() + service.slice(1);
      
      const frequency = requestData?.frequency || 'weekly';
      const isSeasonalContract = frequency === 'seasonal-contract';
      
      return {
        service,
        serviceName,
        price: '',
        duration: '',
        frequency,
        isSeasonalContract,
        // Initialize seasonal contract details if it's a seasonal contract
        // Use customer's payment preference if available
        seasonalContractDetails: isSeasonalContract ? {
          contractStartDate: '',
          contractEndDate: '',
          totalContractPrice: '',
          serviceFrequency: 'weekly',
          paymentSchedule: (requestData?.paymentSchedule as 'single-payment' | 'monthly') || 'single-payment', // Use customer's preference or default
        } : undefined
      };
    });
    
    // Open dialog AFTER setting onboarding status
    setBidDialog({ open: true, requestId, requestData });
    setBidForm({ 
      description: '', 
      servicePricing 
    });
    setBidError('');
    setBidFieldErrors({});
  };

  const handleBidFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBidForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const submitBid = async () => {
    setBidError('');
    setBidLoading(true);
    setBidFieldErrors({});

    // Validate that all services have pricing (duration is optional for now)
    // For seasonal contracts, validate seasonal contract details instead of regular price
    const isPositivePrice = (value: unknown) => parseFloat(String(value ?? '')) > 0;
    const hasInvalidServices = bidForm.servicePricing.some(sp => {
      if (sp.frequency === 'seasonal-contract') {
        return !isPositivePrice(sp.seasonalContractDetails?.totalContractPrice) || 
               !sp.seasonalContractDetails?.contractStartDate || 
               !sp.seasonalContractDetails?.contractEndDate;
      } else {
        return !isPositivePrice(sp.price);
      }
    });
    
    if (hasInvalidServices) {
      const fieldErrors: Record<string, string> = {};
      bidForm.servicePricing.forEach((sp, index) => {
        if (sp.frequency === 'seasonal-contract') {
          if (!isPositivePrice(sp.seasonalContractDetails?.totalContractPrice)) {
            fieldErrors[`servicePricing.${index}.seasonalContractDetails.totalContractPrice`] = 'Please enter a total contract price greater than $0.';
          }
          if (!sp.seasonalContractDetails?.contractStartDate) {
            fieldErrors[`servicePricing.${index}.seasonalContractDetails.contractStartDate`] = 'Please enter a contract start date.';
          }
          if (!sp.seasonalContractDetails?.contractEndDate) {
            fieldErrors[`servicePricing.${index}.seasonalContractDetails.contractEndDate`] = 'Please enter a contract end date.';
          }
        } else {
          if (!isPositivePrice(sp.price)) {
            fieldErrors[`servicePricing.${index}.price`] = 'Please enter a price greater than $0 for this service.';
          }
        }
      });
      setBidFieldErrors(fieldErrors);
      setBidError('Please fill in the highlighted pricing details.');
      setBidLoading(false);
      return;
    }

    // Check if provider has insurance information
    const provider = user as any;
    if (!provider?.insuranceProvider || !provider?.insurancePolicyNumber || !provider?.insuranceExpiryDate) {
      setBidError('Insurance information required. Please complete your insurance information in your profile before submitting bids.');
      setBidFieldErrors({});
      setBidLoading(false);
      return;
    }

    // Check if insurance is not expired
    const today = new Date();
    const insuranceExpiry = new Date(provider.insuranceExpiryDate);
    if (insuranceExpiry < today) {
      setBidError('Your insurance has expired. Please update your insurance information with a current policy before submitting bids.');
      setBidFieldErrors({});
      setBidLoading(false);
      return;
    }

    try {
      // Calculate total price and duration
      // For seasonal contracts, use totalContractPrice; for others, use regular price
      const totalPrice = bidForm.servicePricing.reduce((sum, sp) => {
        if (sp.frequency === 'seasonal-contract' && sp.seasonalContractDetails?.totalContractPrice) {
          return sum + parseFloat(sp.seasonalContractDetails.totalContractPrice);
        } else {
          return sum + (parseFloat(sp.price) || 0);
        }
      }, 0);
      const totalDuration = bidForm.servicePricing.reduce((sum, sp) => sum + (parseFloat(sp.duration) || 0), 0);

      // Use only the provider's additional notes as description
      // Service pricing details are now displayed in the clean table format
      const autoDescription = bidForm.description || 'No additional notes provided.';

      const bidData = {
        requestId: bidDialog.requestId,
        price: totalPrice,
        description: autoDescription,
        estimatedDuration: totalDuration,
        servicePricing: bidForm.servicePricing.map(sp => ({
          ...sp,
          // For seasonal contracts, use totalContractPrice as the price; otherwise use regular price
          price: sp.frequency === 'seasonal-contract' && sp.seasonalContractDetails?.totalContractPrice
            ? parseFloat(sp.seasonalContractDetails.totalContractPrice)
            : parseFloat(sp.price || '0'),
          duration: parseFloat(sp.duration) || 0,
          seasonalContractDetails: sp.seasonalContractDetails ? {
            contractStartDate: new Date(sp.seasonalContractDetails.contractStartDate),
            contractEndDate: new Date(sp.seasonalContractDetails.contractEndDate),
            totalContractPrice: parseFloat(sp.seasonalContractDetails.totalContractPrice),
            paymentSchedule: sp.seasonalContractDetails?.paymentSchedule || 'single-payment', // Use selected payment schedule (defaults to single-payment)
            serviceFrequency: sp.seasonalContractDetails.serviceFrequency || 'weekly'
          } : undefined
        })),
      };

      const response = await apiService.post('/bids', bidData);

      if (response.success) {
        setBidDialog({ open: false, requestId: '', requestData: null });
        setBidForm({ description: '', servicePricing: [] });
        setBidFieldErrors({});
        fetchAvailableRequests(); // Refresh the list
        fetchMyBids(); // Refresh bids list
      }
    } catch (err: any) {
      if (err?.details?.length) {
        const fieldErrors: Record<string, string> = {};
        err.details.forEach((detail: { field?: string; message: string }) => {
          if (detail.field) {
            fieldErrors[detail.field] = detail.message;
          }
        });
        setBidFieldErrors(fieldErrors);
        setBidError(err.message || 'Please fix the highlighted fields.');
      } else {
        setBidFieldErrors({});
        setBidError(err.message || 'Failed to submit bid');
      }
    } finally {
      setBidLoading(false);
    }
  };

  const handleRemoveBid = async (bidId: string) => {
    if (!window.confirm('Are you sure you want to remove this bid? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.delete(`/bids/${bidId}`);
      if (response.success) {
        // Refresh the bids list
        fetchMyBids();
        // Also refresh available requests in case this bid was blocking other requests
        fetchAvailableRequests();
      } else {
        alert('Failed to remove bid. Please try again.');
      }
    } catch (err: any) {
      console.error('Failed to remove bid:', err);
      alert('Failed to remove bid. Please try again.');
    }
  };

  const handleViewSatellite = (address: any) => {
    setSatelliteDialog({ open: true, address });
  };

  const handleViewRequestDetails = (request: any) => {
    setRequestDetailsDialog({ open: true, request });
  };

  const handleScheduleJob = (job: any) => {
    setScheduleDialog({ open: true, job });
    setScheduleForm({ scheduledDate: '', estimatedDuration: '' });
    setScheduleError(null);
    setScheduleLoading(false);
  };

  const handleScheduleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScheduleForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const submitSchedule = async () => {
    // Check if this is a per-instance service - date is optional
    const isPerInstance = scheduleDialog.job ? isPerInstanceJob(scheduleDialog.job) : false;
    
    if (!scheduleForm.estimatedDuration) {
      setScheduleError('Please enter estimated duration');
      return;
    }
    
    // Date is optional for per-instance services
    if (!scheduleForm.scheduledDate && !isPerInstance) {
      setScheduleError('Please select a date');
      return;
    }

    setScheduleLoading(true);
    setScheduleError(null);

    try {
      // For first-time scheduling (no existing scheduledDate), default to permanent change
      // This ensures recurringDayOfWeek is set correctly
      const isFirstTimeScheduling = !scheduleDialog.job?.scheduledDate;
      const requestBody: any = {
        estimatedDuration: parseFloat(scheduleForm.estimatedDuration),
        resetRecurringSchedule: isFirstTimeScheduling ? true : undefined, // Set to true for first-time, undefined for subsequent (user will choose)
      };
      
      // Only include scheduledDate if provided (required for non-per-instance services)
      if (scheduleForm.scheduledDate) {
        requestBody.scheduledDate = scheduleForm.scheduledDate;
      }
      
      // Use longer timeout for schedule operations (30 seconds)
      const response = await apiService.post(
        `/service-requests/${scheduleDialog.job._id}/schedule`, 
        requestBody,
        { timeout: 30000 }
      );

      if (response.success) {
        // Close dialog immediately on success
        setScheduleDialog({ open: false, job: null });
        setScheduleForm({ scheduledDate: '', estimatedDuration: '' });
        setScheduleError(null);
        
        // Use authoritative data from POST response - this is the source of truth from DB
        // This eliminates the need to wait for a potentially slow GET request (refreshSchedules)
        // Same approach as edit schedule - instant update using POST response data
        if (response.data) {
          const scheduledJob = response.data as ScheduleJob;
          console.log('[SCHEDULE] Using job from POST response (same as edit schedule):', {
            jobId: scheduledJob._id,
            scheduledDate: scheduledJob.scheduledDate
          });
          
          // Check if job already exists in the list (update) or is new (add)
          // This handles both initial scheduling and rescheduling existing jobs
          const existingJob = scheduledJobs.find(j => 
            (j._id?.toString() || j.id?.toString()) === (scheduledJob._id?.toString() || scheduledJob.id?.toString())
          );
          
          if (existingJob) {
            // Job exists - update it (same as edit schedule)
            updateJobFromResponse(scheduledJob);
            console.log('[SCHEDULE] Updated existing job in list');
          } else {
            // New job - add it optimistically
            addJobOptimistically(scheduledJob);
            console.log('[SCHEDULE] Added new job to list');
          }
        }
        
        // Dispatch event to notify CustomerManagement to refresh
        window.dispatchEvent(new Event('customer-schedule-updated'));
        window.dispatchEvent(new CustomEvent('refresh-customer-management'));
        
        // Track this update time to prevent periodic refresh from running right after
        lastScheduleUpdateRef.current = Date.now();
        
        // OPTION E: Use acceptedJobs from response (no separate API call needed)
        // Backend returns acceptedJobs list after transaction commits, ensuring data consistency
        // Type assertion: schedule endpoint returns acceptedJobs and acceptedJobsCount
        const scheduleResponse = response as any;
        if (scheduleResponse.acceptedJobs && Array.isArray(scheduleResponse.acceptedJobs)) {
          console.log('[SCHEDULE] Using acceptedJobs from response:', {
            count: scheduleResponse.acceptedJobsCount,
            jobCount: scheduleResponse.acceptedJobs.length,
            note: 'Backend queried after transaction commit - data is consistent'
          });
          setAcceptedJobs(scheduleResponse.acceptedJobs);
        } else {
          // Fallback: If response doesn't include acceptedJobs, fetch separately
          console.warn('[SCHEDULE] Response missing acceptedJobs, falling back to separate API call');
          fetchAcceptedJobs().catch(err => {
            console.error('[SCHEDULE] Error refreshing accepted jobs:', err);
          });
        }
      } else {
        setScheduleError(response.error || response.message || 'Failed to schedule job. Please try again.');
      }
    } catch (err: any) {
      console.error('Failed to schedule job:', err);
      
      // Check if this is a timeout/network error
      const isTimeout = err.isTimeout || err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      const isNetworkError = err.isNetworkError || err.message?.includes('Network') || err.message?.includes('ERR_NETWORK');
      
      if (isTimeout || isNetworkError) {
        // Likely succeeded on backend but timed out - close dialog and refresh
        console.log('[SCHEDULE] Timeout/network error - assuming success, refreshing schedules');
        setScheduleDialog({ open: false, job: null });
        setScheduleForm({ scheduledDate: '', estimatedDuration: '' });
        setScheduleError(null);
        
        // Dispatch events and refresh in background (no message - optimistic update shows change)
        window.dispatchEvent(new Event('customer-schedule-updated'));
        window.dispatchEvent(new CustomEvent('refresh-customer-management'));
        
        // For timeout errors, we can't use POST response data, so we need to refresh
        // But use a delayed refresh to avoid immediate refetch (backend might still be processing)
        setTimeout(() => {
          Promise.all([
            fetchAcceptedJobs(),
            refreshSchedules(true) // Force refresh after timeout (can't use POST response)
          ]).catch(refreshErr => {
            console.error('[SCHEDULE] Error refreshing after timeout:', refreshErr);
          });
        }, 1000); // 1 second delay to let backend finish processing
      } else {
        // Real error - show error message
        setScheduleError(err.message || 'Failed to schedule job. Please try again.');
        setScheduleLoading(false);
      }
    }
  };

  const handleCompleteJob = (job: any) => {
    setCompleteDialog({ open: true, job, completionNotes: '' });
  };

  const handleSkipJob = async (job: any) => {
    const jobId = job?._id?.toString?.() || job?._id;
    if (!jobId) return;
    if (
      !window.confirm(
        'Skip this visit? The customer will not be charged for this visit, and the next recurring service date will be set. The customer will be notified.'
      )
    ) {
      return;
    }
    try {
      const res = (await apiService.post(`/service-requests/${jobId}/skip`, {})) as any;
      if (!res?.success) {
        alert(res?.error || 'Failed to skip visit');
        return;
      }
      await refreshSchedules(true);
      alert(res.message || 'Visit skipped.');
    } catch (e: any) {
      alert(e?.message || 'Failed to skip visit');
    }
  };

  const handleRetryPayment = async (jobId: string) => {
    if (!window.confirm('Retry payment processing for this job?')) {
      return;
    }

    setRetryingPayment(jobId);
    try {
      console.log('[RETRY-PAYMENT] Attempting to retry payment for job:', jobId);
      // Use longer timeout (60 seconds) for payment processing
      const response = await apiService.post(`/payments/retry-payment/${jobId}`, {}, { timeout: 60000 }) as any;
      console.log('[RETRY-PAYMENT] Response received:', response);
      
      if (response.success) {
        // Handle subscription creation for monthly payment contracts
        if (response.subscription?.created) {
          // Subscription was created successfully
          const subscriptionMessage = 'Seasonal Contract Monthly Payment Subscription Plan has been set up on Stripe. Payments will be processed automatically on the 1st of each month.';
          alert(subscriptionMessage);
          
          // Immediately update payment status to 'skip' so the retry button disappears
          setPaymentStatuses((prev) => {
            const updated = new Map(prev);
            updated.set(jobId, {
              status: 'skip',
              amount: 0,
            });
            return updated;
          });
        } else if (response.payment?.status === 'completed' || response.message?.includes('already successfully processed')) {
          // Regular payment completed
          const message = response.message || 'Payment was already successfully processed!';
          alert(message);
          
          // Update payment status
          setPaymentStatuses((prev) => {
            const updated = new Map(prev);
            updated.set(jobId, {
              status: 'completed',
              amount: response.payment?.amount || 0,
            });
            return updated;
          });
        } else if (response.payment?.status === 'pending') {
          alert('Payment is pending. It may take a few moments to process.');
        } else if (response.payment?.status === 'skip') {
          // Payment was skipped (monthly subscription already exists)
          const message = response.message || 'Payment skipped - subscription already exists';
          alert(message);
          
          // Update payment status to 'skip'
          setPaymentStatuses((prev) => {
            const updated = new Map(prev);
            updated.set(jobId, {
              status: 'skip',
              amount: 0,
            });
            return updated;
          });
        } else {
          alert(`Payment status: ${response.payment?.status || 'unknown'}`);
        }
        
        // Refresh payment statuses and jobs
        await refreshSchedules();
        // Payment statuses will be refreshed automatically via useEffect
      } else {
        console.error('[RETRY-PAYMENT] Response indicates failure:', response);
        alert(response.error || response.message || 'Failed to retry payment');
      }
    } catch (err: any) {
      console.error('[RETRY-PAYMENT] Exception caught:', {
        message: err.message,
        error: err.error,
        status: err.status,
        raw: err.raw,
        fullError: err
      });
      
      // Check if payment is already completed (from payment statuses)
      const payment = paymentStatuses.get(jobId);
      if (payment?.status === 'completed') {
        alert('Payment is already completed. No retry needed.');
      } else {
        // Check if it's a timeout/network error
        const isTimeout = err.message?.includes('timeout') || 
                         err.message?.includes('Network Error') || 
                         err.message?.includes('ERR_NETWORK');
        
        // Check if there's a response with error details
        const hasResponse = err.response || err.status;
        const serverError = err.response?.data?.error || err.response?.data?.message;
        
        if (isTimeout && !hasResponse) {
          // True timeout - no response from server
          alert('Payment processing is taking longer than expected. The payment may still be processing. Please refresh the page in a moment to check the payment status.');
        } else if (serverError) {
          // Server returned an error
          alert(`Payment retry failed: ${serverError}`);
        } else {
          // Other error
          const errorMessage = err.message || err.error || 'Failed to retry payment. Please try again.';
          alert(`Payment retry failed: ${errorMessage}`);
        }
      }
    } finally {
      setRetryingPayment(null);
    }
  };

  const submitCompletion = async () => {
    try {
      // Mark the job as completed - backend handles payment processing
      const response = await apiService.post(`/service-requests/${completeDialog.job._id}/complete`, {
        completionNotes: completeDialog.completionNotes,
      });

      if (response.success) {
        // Backend completion endpoint handles all payment processing including:
        // - Seasonal contract first payments
        // - Regular recurring service payments
        // - Onboarded customer payments
        // The response message indicates if payment was processed
        
        // Check for payment errors
        const responseAny = response as any;
        if (responseAny.paymentError || (responseAny.payment && responseAny.payment.status !== 'completed' && responseAny.payment.status !== 'skip')) {
          const errorMessage = responseAny.paymentError?.reason || 
                              responseAny.error || 
                              (responseAny.payment?.status === 'failed' ? 'Payment processing failed' : 'Payment is pending');
          alert(`Job completed, but payment ${responseAny.payment?.status === 'failed' ? 'failed' : 'is pending'}: ${errorMessage}. Please retry payment from Today's Jobs if needed.`);
        } else if (response.message) {
          console.log('Job completed:', response.message);
        }

        setCompleteDialog({ open: false, job: null, completionNotes: '' });
        await refreshSchedules();
        // Trigger CustomerManagement refresh to update last/next service dates
        setCustomerManagementRefreshTrigger(prev => prev + 1);
      } else {
        // Handle completion error
        alert(`Failed to complete job: ${response.error || response.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      console.error('Failed to complete job:', err);
      alert(`Failed to complete job: ${err.message || 'Unknown error'}`);
    }
  };

  const handleEditSchedule = (job: any) => {
    console.log('[SCHEDULE-EDIT] handleEditSchedule called:', {
      jobId: job._id,
      scheduledDate: job.scheduledDate,
      customer: job.customerId ? `${job.customerId.firstName} ${job.customerId.lastName}` : 'Unknown'
    });

    // Convert scheduledDate (ISO string or Date) to YYYY-MM-DD format for date input
    let dateString = '';
    if (job.scheduledDate) {
      const date = new Date(job.scheduledDate);
      // Extract UTC date components to get the calendar date
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      dateString = `${year}-${month}-${day}`;
    }
    
    console.log('[SCHEDULE-EDIT] Opening edit dialog with:', {
      dateString,
      hasJob: !!job
    });

    setEditScheduleDialog({ 
      open: true, 
      job, 
      newDate: dateString, 
      isDayChange: false 
    });
  };

  // Check for day change when date field changes (immediate feedback)
  // For weekly/bi-weekly: checks day of week changes
  // For monthly: checks day of month changes
  const checkDayChangeForEditSchedule = (newDate: string): boolean => {
    if (!editScheduleDialog.job || !newDate) return false;
    
    let jobFrequency = editScheduleDialog.job.frequency || 
                        editScheduleDialog.job.serviceType?.frequency ||
                        null;
    
    // For seasonal contracts, extract the actual serviceFrequency from seasonalContractDetails
    if (jobFrequency === 'seasonal-contract') {
      const serviceCategory = editScheduleDialog.job.serviceType?.category;
      
      // First check acceptedBid (for customers with bids)
      if (editScheduleDialog.job.acceptedBid?.servicePricing) {
        const matchingServicePricing = editScheduleDialog.job.acceptedBid.servicePricing.find((sp: any) => {
          if (serviceCategory === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
          if (serviceCategory === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
          if (serviceCategory === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
          return false;
        });
        
        if (matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
          jobFrequency = matchingServicePricing.seasonalContractDetails.serviceFrequency;
          console.log('[SCHEDULE-EDIT] Extracted serviceFrequency from acceptedBid seasonal contract:', jobFrequency);
        }
      }
      
      // Check customer.servicePricing (for onboarded customers without bids)
      // CRITICAL: Backend now includes servicePricing in job.customerId, so this should always be available
      if (jobFrequency === 'seasonal-contract' && editScheduleDialog.job.customerId) {
        const customer = typeof editScheduleDialog.job.customerId === 'object' ? editScheduleDialog.job.customerId : null;
        if (customer?.servicePricing) {
          let customerPricingData: any = null;
          if (serviceCategory === 'mowing' && customer.servicePricing.lawnMowingTrimming) {
            customerPricingData = customer.servicePricing.lawnMowingTrimming;
          } else if (serviceCategory === 'fertilizing' && customer.servicePricing.weedControlFertilizing) {
            customerPricingData = customer.servicePricing.weedControlFertilizing;
          } else if (serviceCategory === 'snow_removal' && customer.servicePricing.snowRemoval) {
            customerPricingData = customer.servicePricing.snowRemoval;
          }
          
          if (customerPricingData?.seasonalContractDetails?.serviceFrequency) {
            jobFrequency = customerPricingData.seasonalContractDetails.serviceFrequency;
            console.log('[SCHEDULE-EDIT] Extracted serviceFrequency from customer.servicePricing seasonal contract:', jobFrequency);
          }
        } else {
          console.warn('[SCHEDULE-EDIT] Seasonal contract job missing customer.servicePricing - backend should include this:', {
            jobId: editScheduleDialog.job._id,
            hasCustomerId: !!editScheduleDialog.job.customerId,
            customerIdType: typeof editScheduleDialog.job.customerId,
            serviceCategory
          });
        }
      }
    }
    
    // Skip per-instance services - they don't have recurring schedules
    if (jobFrequency === 'per-instance') {
      return false;
    }
    
    // Get original scheduled date
    const originalDate = editScheduleDialog.job.scheduledDate;
    if (!originalDate) {
      return false; // No original date, skip
    }
    
    // Convert original date to YYYY-MM-DD format for comparison
    const originalDateObj = new Date(originalDate);
    const originalYear = originalDateObj.getUTCFullYear();
    const originalMonth = String(originalDateObj.getUTCMonth() + 1).padStart(2, '0');
    const originalDay = String(originalDateObj.getUTCDate()).padStart(2, '0');
    const originalDateStr = `${originalYear}-${originalMonth}-${originalDay}`;
    
    // Helper to get day of week from date string
    const getDayOfWeek = (dateStr: string): number => {
      if (!dateStr) return -1;
      const date = new Date(dateStr + 'T00:00:00');
      return date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    };
    
    // Helper to get day of month from date string
    const getDayOfMonth = (dateStr: string): number => {
      if (!dateStr) return -1;
      const date = new Date(dateStr + 'T00:00:00');
      return date.getDate(); // Returns 1-31
    };
    
    // For monthly services, check day of month changes
    if (jobFrequency === 'monthly') {
      const oldDayOfMonth = getDayOfMonth(originalDateStr);
      const newDayOfMonth = getDayOfMonth(newDate);
      
      // Day of month changed if both days are valid and different
      return oldDayOfMonth !== -1 && newDayOfMonth !== -1 && oldDayOfMonth !== newDayOfMonth;
    }
    
    // For weekly/bi-weekly services, check day of week changes
    if (jobFrequency === 'weekly' || jobFrequency === 'bi-weekly') {
      const oldDay = getDayOfWeek(originalDateStr);
      const newDay = getDayOfWeek(newDate);
      
      // Day changed if both days are valid and different
      return oldDay !== -1 && newDay !== -1 && oldDay !== newDay;
    }
    
    // For other frequencies, don't check for day changes
    return false;
  };

  const submitScheduleEdit = async () => {
    console.log('[SCHEDULE-EDIT] submitScheduleEdit called:', {
      hasJob: !!editScheduleDialog.job,
      hasNewDate: !!editScheduleDialog.newDate,
      jobId: editScheduleDialog.job?._id,
      newDate: editScheduleDialog.newDate,
      job: editScheduleDialog.job,
      dayResetDialogOpen: dayResetDialog.open
    });

    if (!editScheduleDialog.job || !editScheduleDialog.newDate) {
      console.error('[SCHEDULE-EDIT] Missing required data:', {
        job: editScheduleDialog.job,
        newDate: editScheduleDialog.newDate
      });
      return;
    }

    // No-op if date unchanged
    const originalDate = editScheduleDialog.job.scheduledDate;
    if (originalDate) {
      const originalDateObj = new Date(originalDate);
      const originalDateStr = `${originalDateObj.getUTCFullYear()}-${String(originalDateObj.getUTCMonth() + 1).padStart(2, '0')}-${String(originalDateObj.getUTCDate()).padStart(2, '0')}`;
      if (editScheduleDialog.newDate === originalDateStr) {
        setEditScheduleDialog({ open: false, job: null, newDate: '', isDayChange: false });
        return;
      }
    }

    // If day change dialog is open, don't proceed (user needs to make choice first)
    if (dayResetDialog.open) {
      console.log('[SCHEDULE-EDIT] Day change dialog is open, waiting for user choice...');
      return;
    }

    // Check if this is a per-instance service - if so, skip dialog and save directly
    const jobFrequency = editScheduleDialog.job.frequency || 
                        editScheduleDialog.job.serviceType?.frequency ||
                        null;
    
    if (jobFrequency === 'per-instance') {
      console.log('[SCHEDULE-EDIT] Per-instance service detected - skipping dialog and saving directly');
      try {
        await updateScheduleDate(editScheduleDialog.job, editScheduleDialog.newDate, false);
      } catch (err: any) {
        console.error('[SCHEDULE-EDIT] Error updating per-instance service:', err);
      }
      return;
    }

    // Fallback check: If day changed and we haven't shown dialog yet (should rarely happen)
    const dayChanged = checkDayChangeForEditSchedule(editScheduleDialog.newDate);
    console.error('[SCHEDULE-EDIT] checkDayChangeForEditSchedule result:', {
      dayChanged,
      jobFrequency: editScheduleDialog.job.frequency,
      serviceCategory: editScheduleDialog.job.serviceType?.category,
      hasAcceptedBid: !!editScheduleDialog.job.acceptedBid,
      hasCustomerId: !!editScheduleDialog.job.customerId,
      customerIdType: typeof editScheduleDialog.job.customerId,
      customerServicePricing: typeof editScheduleDialog.job.customerId === 'object' ? Object.keys(editScheduleDialog.job.customerId?.servicePricing || {}) : null,
      newDate: editScheduleDialog.newDate,
      originalDate: editScheduleDialog.job.scheduledDate
    });
    
    if (dayChanged) {
      console.log('[SCHEDULE-EDIT] Day change detected in submitScheduleEdit (fallback), showing dialog');
    setDayResetDialog({
      open: true,
      job: editScheduleDialog.job,
      newDate: editScheduleDialog.newDate,
      resetSchedule: false
    });
    setEditScheduleDialog({ open: false, job: null, newDate: '', isDayChange: false });
      return;
    }

    // No day change - proceed with one-time update (resetSchedule: false)
    try {
      await updateScheduleDate(editScheduleDialog.job, editScheduleDialog.newDate, false);
    } catch (err: any) {
      console.error('[SCHEDULE-EDIT] Error updating schedule:', err);
    }
  };

  const updateScheduleDate = async (job: any, newDate: string, resetSchedule: boolean) => {
    try {
      setScheduleUpdateError(null);
      setScheduleUpdateSuccess(null);
      setScheduleUpdateLoading(true);
      
      console.log('[UPDATE-SCHEDULE] Updating schedule:', {
        jobId: job._id?.toString(),
        jobIdShort: job._id?.toString()?.slice(-8),
        currentScheduledDate: job.scheduledDate,
        newDate,
        resetSchedule,
        serviceType: job.serviceType?.name || job.serviceType?.category || 'Unknown',
        customer: job.customerId ? `${job.customerId.firstName || ''} ${job.customerId.lastName || ''}`.trim() : 'Unknown'
      });
      
      // CRITICAL: Get expected customer ID for verification
      // This MUST match the service request's customerId on the backend
      const expectedCustomerId = job.customerId?._id?.toString() || job.customerId?.toString();
      
      // DEFENSIVE CHECK: Verify we have a customer ID before proceeding
      if (!expectedCustomerId) {
        const errorMsg = 'Cannot update schedule: Customer ID is missing. Please refresh and try again.';
        console.error('[UPDATE-SCHEDULE] CRITICAL ERROR:', errorMsg, {
          jobId: job._id?.toString(),
          job: job
        });
        setScheduleUpdateError(errorMsg);
        setScheduleUpdateLoading(false);
        return;
      }

      console.log('[UPDATE-SCHEDULE] Sending schedule update with customer verification:', {
        jobId: job._id?.toString(),
        scheduledDate: newDate,
        expectedCustomerId,
        customerName: job.customerId ? `${job.customerId.firstName || ''} ${job.customerId.lastName || ''}`.trim() : 'Unknown',
        serviceRequestCustomerId: job.customerId?._id?.toString() || job.customerId?.toString()
      });

      // Use longer timeout for schedule updates (30 seconds) since they may involve database operations
      const response = await apiService.post(`/service-requests/${job._id}/schedule`, {
        scheduledDate: newDate,
        resetRecurringSchedule: resetSchedule,
        estimatedDuration: job.estimatedDuration || 1, // Include estimatedDuration if available
        expectedCustomerId: expectedCustomerId // CRITICAL: Send expected customer ID for verification
      }, { timeout: 30000 });

      console.log('[UPDATE-SCHEDULE] Response:', {
        success: response.success,
        error: response.error,
        message: response.message,
        data: response.data,
        fullResponse: response
      });

      if (response.success && response.data) {
        // Use authoritative data from POST response - this is the source of truth from DB
        // This eliminates the need to wait for a potentially stale/cached GET request
        const updatedJob = response.data as any;
        
        console.log('[UPDATE-SCHEDULE] Received updated job from backend:', {
          jobId: updatedJob._id?.toString() || updatedJob.id?.toString(),
          scheduledDate: updatedJob.scheduledDate,
          status: updatedJob.status,
        });
        
        // Update state immediately with authoritative data from backend
        updateJobFromResponse(updatedJob);
        
        // Track this update time to prevent periodic refresh from running right after
        lastScheduleUpdateRef.current = Date.now();
        
        // Close dialogs immediately on success (optimistic UI update)
        setEditScheduleDialog({ open: false, job: null, newDate: '', isDayChange: false });
        setDayResetDialog({ open: false, job: null, newDate: '', resetSchedule: false });
        
        // Show brief success message
        const successMessage = resetSchedule 
          ? 'Schedule updated successfully!'
          : 'Schedule updated successfully!';
        setScheduleUpdateSuccess(successMessage);
        
        // Clear success message after 2 seconds
        setTimeout(() => setScheduleUpdateSuccess(null), 2000);
        
        // No refresh needed - updateJobFromResponse already updated state with authoritative data from POST response
        // The schedule is already updated immediately, so no refresh is necessary
        // Other sections (accepted jobs, customer management) will refresh on their next periodic interval
        // Periodic refresh will also skip schedule refresh for 30 seconds to prevent the delay
        
        console.log('[UPDATE-SCHEDULE] Schedule update completed successfully - state updated from POST response, no refresh needed');
      } else {
        console.error('[UPDATE-SCHEDULE] Update failed:', response.error || response.message);
        const errorMessage = response.error || response.message || 'Failed to update schedule';
        setScheduleUpdateError(errorMessage);
        // Don't close dialog on error - let user try again or cancel
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      console.error('[UPDATE-SCHEDULE] Exception caught:', err);
      console.error('[UPDATE-SCHEDULE] Error details:', {
        message: err.message,
        response: err.response,
        status: err.status,
        data: err.data,
        isTimeout: err.code === 'ECONNABORTED' || err.message?.includes('timeout'),
        isNetworkError: !err.response && err.request
      });
      
      // Check if this is a timeout or network error - the update may have succeeded on the backend
      // Check both the ApiError properties and the original error properties
      const isTimeout = err.isTimeout || err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('ERR_NETWORK');
      const isNetworkError = err.isNetworkError || (!err.response && err.request) || err.message?.includes('Network error');
      
      if (isTimeout || isNetworkError) {
        // For timeout/network errors, the update may have succeeded on the backend
        // Use optimistic update since we can't get POST response
        console.log('[UPDATE-SCHEDULE] Timeout/network error detected - using optimistic update');
        
        // Parse new date for optimistic update
        const newScheduledDate = newDate ? new Date(newDate + 'T00:00:00Z').toISOString() : job.scheduledDate;
        
        // Optimistic UI update - update local state immediately
        updateJobOptimistically(job._id?.toString() || '', {
          scheduledDate: newScheduledDate,
          scheduledTime: job.scheduledTime,
        });
        
        // Close dialogs immediately (update may have succeeded)
        setEditScheduleDialog({ open: false, job: null, newDate: '', isDayChange: false });
        setDayResetDialog({ open: false, job: null, newDate: '', resetSchedule: false });
        
        // Single refresh after a brief delay to verify (don't spam refreshes)
        setTimeout(() => {
          refreshSchedules(true).catch(err => {
            console.error('[UPDATE-SCHEDULE] Error refreshing schedules after timeout:', err);
          });
        }, 500);
        
        // Run other refreshes in parallel (don't await)
        fetchAcceptedJobs();
        setCustomerManagementRefreshTrigger(prev => prev + 1);
      } else {
        // For actual errors (not timeout), show error and keep dialog open
        setScheduleUpdateError(err.message || 'Failed to update schedule. Please try again.');
      }
      
      // Clear error message after 8 seconds (longer for timeout messages)
      setTimeout(() => setScheduleUpdateError(null), 8000);
    } finally {
      setScheduleUpdateLoading(false);
    }
  };

  const handleDayResetConfirm = async () => {
    await updateScheduleDate(dayResetDialog.job, dayResetDialog.newDate, dayResetDialog.resetSchedule);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Seasonal Renewal Match Notifications */}
      <ProviderMatchNotification />
      <IncompleteStopsResolver onResolved={() => refreshSchedules(true)} />

      {/* W-9 Status Alert - Block payouts if missing */}
      {user && (user as any).userType === 'provider' && !(user as any).w9OnFile && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => navigate('/provider/w9-upload')}
            >
              Upload W-9
            </Button>
          }
        >
          <Typography variant="body2" fontWeight="bold">
            W-9 Form Required
          </Typography>
          <Typography variant="body2">
            IRS requires a quick W-9 before we can pay you — takes 30 seconds. Payouts are blocked until W-9 is on file.
          </Typography>
        </Alert>
      )}

      {/* Backup Withholding Warning */}
      {user && (user as any).userType === 'provider' && (user as any).backupWithholdingEnabled && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" fontWeight="bold">
            24% Backup Withholding Active
          </Typography>
          <Typography variant="body2">
            Your Tax ID (TIN) is missing or invalid. 24% of your payouts are being withheld for backup withholding. 
            Total withheld: ${((user as any).totalBackupWithheld || 0).toFixed(2)}. 
            Please update your W-9 with a valid TIN to stop backup withholding.
          </Typography>
        </Alert>
      )}

      {/* Pesticide License Alert - Required for Weed Control/Fertilizing */}
      {user && (user as any).userType === 'provider' && 
       ((user as any).services?.includes('weedControlFertilizing') || (user as any).services?.some((s: any) => s === 'weedControlFertilizing' || (typeof s === 'object' && s.id === 'weedControlFertilizing'))) &&
       !(user as any).pesticideLicenseOnFile && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => navigate('/provider/pesticide-license-upload')}
            >
              Upload License
            </Button>
          }
        >
          <Typography variant="body2" fontWeight="bold">
            Pesticide License Required
          </Typography>
          <Typography variant="body2">
            You offer Weed Control/Fertilizing services but haven't uploaded your IDOA Commercial Pesticide Applicator License. 
            You cannot bid on weed control service requests until your license is on file.
          </Typography>
        </Alert>
      )}
      
      {/* Schedule Update Success/Error Messages */}
      {scheduleUpdateSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setScheduleUpdateSuccess(null)}>
          {scheduleUpdateSuccess}
        </Alert>
      )}
      {scheduleUpdateError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleUpdateError(null)}>
          {scheduleUpdateError}
        </Alert>
      )}
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome back, {user?.firstName}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your bids, jobs, and track your business performance
        </Typography>
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeSection} 
          onChange={(_, newValue) => setActiveSection(newValue)}
          aria-label="dashboard navigation"
        >
          <Tab 
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Dashboard
                <InfoTip title="Home base: today’s jobs, this week’s schedule, and marketplace requests." />
              </Box>
            }
            value="dashboard" 
            icon={<Dashboard />} 
            iconPosition="start"
            data-tour="demo-tab-dashboard"
          />
          <Tab 
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Customer Management
                <InfoTip title="Onboard existing customers, manage properties/services, and send invitations (typically 1% fee)." />
              </Box>
            }
            value="customer-management" 
            icon={<People />} 
            iconPosition="start"
            data-tour="demo-tab-customers"
          />
          <Tab 
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Scheduling
                <InfoTip title="Put work on the calendar: Add to Today, Move Day, Add All, and Edit Schedules." />
              </Box>
            }
            value="scheduling" 
            icon={<Schedule />} 
            iconPosition="start"
            data-tour="demo-tab-scheduling"
          />
          <Tab 
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Route Optimization
                <InfoTip title="Build crew drive order for a day: Sort and Optimize, adjust stops, Save Routes." />
              </Box>
            }
            value="route-optimization" 
            icon={<Route />} 
            iconPosition="start"
            data-tour="demo-tab-routes"
          />
        </Tabs>
      </Box>

      {!!(user as any)?.isDemo && (
        <DemoGuidedTour
          enabled
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
      )}

      {/* Customer Management Section */}
      {activeSection === 'customer-management' && (
        <CustomerManagement providerId={user?._id || user?.id || ''} refreshTrigger={customerManagementRefreshTrigger} />
      )}

      {/* Scheduling Section */}
      {activeSection === 'scheduling' && (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              Scheduling
              <InfoTip title="Tools to place and move services on the calendar before you optimize routes." />
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Schedule />}
                onClick={() => setAddToScheduleDialogOpen(true)}
                data-tour="demo-sched-add-today"
              >
                Add to Today
              </Button>
              <InfoTip title="Bring accepted or unscheduled services onto today’s schedule." />
              <Button
                variant="contained"
                startIcon={<SwapHoriz />}
                onClick={() => setMoveDayDialogOpen(true)}
                data-tour="demo-sched-move-day"
              >
                Move Day
              </Button>
              <InfoTip title="Move all services from one date to another (handy for weather)." />
              <Button
                variant="outlined"
                startIcon={<Schedule />}
                onClick={() => setAddAllDialogOpen(true)}
                data-tour="demo-sched-add-all"
              >
                Add All
              </Button>
              <InfoTip title="Schedule all unscheduled services of one type onto a chosen date." />
              <Button
                variant="contained"
                startIcon={<Schedule />}
                onClick={() => {
                  setCustomerScheduleDefaultDate(undefined);
                  setCustomerScheduleDialogOpen(true);
                }}
                data-tour="demo-sched-edit"
              >
                Edit Schedules
              </Button>
              <InfoTip title="Change next service dates or recurring patterns per customer." />
            </Box>
          </Box>
          
          {/* Accepted Jobs Needing Scheduling */}
          {acceptedJobs.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Accepted Jobs - Schedule Required
                <InfoTip title="Won marketplace jobs that still need a calendar date — use Schedule or Add to Today." />
              </Typography>
              {/* Per-instance notification */}
              {(() => {
                console.log('[PER-INSTANCE-NOTIFICATION] Checking acceptedJobs:', {
                  totalJobs: acceptedJobs.length,
                  dismissedSet: Array.from(dismissedPerInstanceNotifications),
                  jobs: acceptedJobs.map((job: any) => ({
                    jobId: (job._id || job.id || '').toString(),
                    frequency: job.frequency,
                    hasAcceptedBid: !!job.acceptedBid,
                    serviceCategory: job.serviceType?.category
                  }))
                });
                
                const shouldShow = acceptedJobs.some(job => {
                  const jobId = (job._id || job.id || '').toString();
                  const isPerInstance = isPerInstanceJob(job);
                  const isDismissed = dismissedPerInstanceNotifications.has(jobId);
                  if (isPerInstance && !isDismissed) {
                    console.log('[PER-INSTANCE-NOTIFICATION] Should show notification for job:', {
                      jobId,
                      isPerInstance,
                      isDismissed,
                      frequency: job.frequency,
                      acceptedBid: job.acceptedBid ? 'present' : 'missing',
                      servicePricing: job.acceptedBid?.servicePricing?.length || 0
                    });
                  }
                  return isPerInstance && !isDismissed;
                });
                
                console.log('[PER-INSTANCE-NOTIFICATION] Should show notification?', shouldShow);
                return shouldShow;
              })() && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 2 }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        // Dismiss all per-instance notifications
                        acceptedJobs.forEach(job => {
                          if (isPerInstanceJob(job)) {
                            const jobId = (job._id || job.id || '').toString();
                            dismissPerInstanceNotification(jobId);
                          }
                        });
                      }}
                    >
                      <Close fontSize="inherit" />
                    </IconButton>
                  }
                >
                  <Typography variant="body2">
                    <strong>Note:</strong> Some of these jobs are <strong>Per Instance</strong> services. 
                    These services don't require initial scheduling - you can schedule them on-demand when needed 
                    (e.g., snow removal when it snows). You can still schedule them now if you prefer.
                  </Typography>
                </Alert>
              )}
              <List>
                {acceptedJobs.map((job) => (
                  <ListItem key={job._id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">
                            {/* Show only the service that matches this ServiceRequest's serviceType */}
                            {(() => {
                              // Map serviceType category to servicePricing key
                              const serviceCategory = job.serviceType?.category;
                              let matchingServiceKey: string | null = null;
                              if (serviceCategory === 'mowing') {
                                matchingServiceKey = 'lawnMowingTrimming';
                              } else if (serviceCategory === 'fertilizing') {
                                matchingServiceKey = 'weedControlFertilizing';
                              } else if (serviceCategory === 'snow_removal') {
                                matchingServiceKey = 'snowRemoval';
                              }
                              
                              // Find matching service in servicePricing array
                              if (job.acceptedBid?.servicePricing && matchingServiceKey) {
                                const matchingService = job.acceptedBid.servicePricing.find((sp: any) => 
                                  sp.service === matchingServiceKey || 
                                  sp.serviceName?.toLowerCase().includes(serviceCategory || '')
                                );
                                if (matchingService) {
                                  return normalizeServiceName(matchingService.serviceName || matchingService.service);
                                }
                              }
                              
                              // Fallback to ServiceRequest's serviceType name
                              return formatServiceTypeName(job.serviceType?.name);
                            })()}
                          </Typography>
                          <Chip label="Needs Scheduling" color="warning" size="small" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {job.address?.street}, {job.address?.city}, {job.address?.state}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            Customer: {job.customerId?.firstName} {job.customerId?.lastName}
                          </Typography>
                          {/* Show only the service pricing that matches this ServiceRequest's serviceType */}
                          {(() => {
                            // Map serviceType category to servicePricing key
                            const serviceCategory = job.serviceType?.category;
                            let matchingServiceKey: string | null = null;
                            if (serviceCategory === 'mowing') {
                              matchingServiceKey = 'lawnMowingTrimming';
                            } else if (serviceCategory === 'fertilizing') {
                              matchingServiceKey = 'weedControlFertilizing';
                            } else if (serviceCategory === 'snow_removal') {
                              matchingServiceKey = 'snowRemoval';
                            }
                            
                            // Find matching service in servicePricing array
                            if (job.acceptedBid?.servicePricing && matchingServiceKey) {
                              const matchingService = job.acceptedBid.servicePricing.find((sp: any) => 
                                sp.service === matchingServiceKey || 
                                sp.serviceName?.toLowerCase().includes(serviceCategory || '')
                              );
                              if (matchingService) {
                                // For seasonal contracts, check the actual serviceFrequency
                                let displayFrequency = matchingService.frequency;
                                if (matchingService.frequency === 'seasonal-contract' && matchingService.seasonalContractDetails?.serviceFrequency) {
                                  displayFrequency = matchingService.seasonalContractDetails.serviceFrequency;
                                }
                                
                                // Format frequency for display
                                const frequencyLabel = displayFrequency === 'per-instance' ? 'Per Instance' :
                                                      displayFrequency === 'weekly' ? 'Weekly' :
                                                      displayFrequency === 'bi-weekly' ? 'Bi-Weekly' :
                                                      displayFrequency === 'monthly' ? 'Monthly' :
                                                      displayFrequency === 'seasonal-contract' ? 'Seasonal Contract' :
                                                      displayFrequency;
                                
                                return (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {normalizeServiceName(matchingService.serviceName || matchingService.service)}: {formatCurrency(matchingService.price)} ({frequencyLabel})
                                    </Typography>
                                  </Box>
                                );
                              }
                            }
                            return null;
                          })()}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {isPerInstanceJob(job) && (
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={async () => {
                              try {
                                const response = await apiService.post(`/service-requests/${job._id}/dismiss-schedule`);
                                if (response.success) {
                                  // Refresh the accepted jobs list
                                  await fetchAcceptedJobs();
                                } else {
                                  console.error('Failed to dismiss service:', response.error);
                                }
                              } catch (err: any) {
                                console.error('Failed to dismiss service:', err);
                              }
                            }}
                          >
                            Schedule Later as Needed
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          startIcon={<Schedule />}
                          onClick={() => handleScheduleJob(job)}
                        >
                          Schedule
                        </Button>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} data-tour="demo-todays-jobs">
              <TodaysJobs
                todaysJobs={todaysJobs}
                onCompleteJob={handleCompleteJob}
                onSkipJob={handleSkipJob}
                onRetryPayment={handleRetryPayment}
                paymentStatuses={paymentStatuses}
                retryingPayment={retryingPayment}
                formatServiceTypeName={formatServiceTypeName}
                getStatusColor={getStatusColor}
              />
            </Grid>
            <Grid item xs={12} md={6} data-tour="demo-this-weeks-schedule">
              <ThisWeeksSchedule
                scheduledJobs={scheduledJobs}
                onEditSchedule={handleEditSchedule}
                formatServiceTypeName={formatServiceTypeName}
              />
            </Grid>
          </Grid>
          <AddToTodayDialog
            open={addToScheduleDialogOpen}
            onClose={() => setAddToScheduleDialogOpen(false)}
              onSuccess={() => {
              // Show success message and keep it visible until refresh completes
              setScheduleUpdateSuccess('Service added to today\'s schedule. Refreshing...');
              
              // Dispatch events to notify Admin view and other components
              window.dispatchEvent(new Event('customer-schedule-updated'));
              window.dispatchEvent(new Event('refresh-todays-schedule'));
              window.dispatchEvent(new CustomEvent('refresh-customer-management'));
              
              // Refresh immediately (force refresh to avoid delays)
              Promise.all([
                refreshSchedules(true).catch(err => {
                  console.error('[ADD-TO-TODAY] Error refreshing schedules:', err);
                }),
                Promise.resolve(fetchAcceptedJobs()).catch(err => {
                  console.error('[ADD-TO-TODAY] Error fetching accepted jobs:', err);
                })
              ]).then(() => {
                // Data refreshed - update message and clear after 3 seconds
                setScheduleUpdateSuccess('Service added successfully! Schedule updated.');
                setTimeout(() => setScheduleUpdateSuccess(null), 3000);
              }).catch(() => {
                // On error, clear message after 5 seconds
                setTimeout(() => setScheduleUpdateSuccess(null), 5000);
              });
              
              setCustomerManagementRefreshTrigger(prev => prev + 1);
            }}
          />
          <MoveDayDialog
            open={moveDayDialogOpen}
            onClose={() => setMoveDayDialogOpen(false)}
            onSuccess={() => {
              setScheduleUpdateSuccess('Move day complete. Refreshing...');
              window.dispatchEvent(new Event('customer-schedule-updated'));
              window.dispatchEvent(new Event('refresh-todays-schedule'));
              window.dispatchEvent(new CustomEvent('refresh-customer-management'));
              Promise.all([
                refreshSchedules(true).catch((err) => console.error('[MOVE-DAY] Refresh error:', err)),
                Promise.resolve(fetchAcceptedJobs()).catch((err) => console.error('[MOVE-DAY] Fetch accepted jobs error:', err)),
              ]).then(() => {
                setScheduleUpdateSuccess('Move day complete!');
                setTimeout(() => setScheduleUpdateSuccess(null), 3000);
              }).catch(() => setTimeout(() => setScheduleUpdateSuccess(null), 5000));
              setCustomerManagementRefreshTrigger((prev) => prev + 1);
            }}
          />
          <AddAllToScheduleDialog
            open={addAllDialogOpen}
            onClose={() => setAddAllDialogOpen(false)}
            onSuccess={() => {
              setScheduleUpdateSuccess('Batch schedule complete. Refreshing...');
              window.dispatchEvent(new Event('customer-schedule-updated'));
              window.dispatchEvent(new Event('refresh-todays-schedule'));
              window.dispatchEvent(new CustomEvent('refresh-customer-management'));
              Promise.all([
                refreshSchedules(true).catch((err) => console.error('[ADD-ALL] Refresh error:', err)),
                Promise.resolve(fetchAcceptedJobs()).catch((err) => console.error('[ADD-ALL] Fetch accepted jobs error:', err)),
              ]).then(() => {
                setScheduleUpdateSuccess('Batch schedule complete!');
                setTimeout(() => setScheduleUpdateSuccess(null), 3000);
              }).catch(() => setTimeout(() => setScheduleUpdateSuccess(null), 5000));
              setCustomerManagementRefreshTrigger((prev) => prev + 1);
            }}
          />
          <CustomerScheduleDialog
            open={customerScheduleDialogOpen}
            onClose={() => setCustomerScheduleDialogOpen(false)}
            onSuccess={(updatedService?: any) => {
              // If we have the updated service from API response, update UI immediately
              if (updatedService) {
                console.log('[CUSTOMER-SCHEDULE] Updating UI immediately with service from response:', updatedService);
                // Use updateJobFromResponse to immediately show the scheduled service
                updateJobFromResponse(updatedService);
                setScheduleUpdateSuccess('Schedule updated successfully!');
                setTimeout(() => setScheduleUpdateSuccess(null), 3000);
              } else {
                // Fallback: Show message and refresh if no service data provided
                setScheduleUpdateSuccess('Schedule updated. Refreshing...');
              }
              
              // Dispatch events to notify Admin view and other components
              window.dispatchEvent(new Event('customer-schedule-updated'));
              window.dispatchEvent(new Event('refresh-todays-schedule'));
              window.dispatchEvent(new CustomEvent('refresh-customer-management'));
              
              // Refresh in background to ensure consistency (non-blocking)
              // If we already updated optimistically, this will just sync any edge cases
              refreshSchedules(true).catch(err => {
                console.error('[CUSTOMER-SCHEDULE] Error refreshing schedules:', err);
              });
              fetchAcceptedJobs().catch(err => {
                console.error('[CUSTOMER-SCHEDULE] Error fetching accepted jobs:', err);
              });
              
              // If we didn't have service data, wait for refresh to complete
              if (!updatedService) {
                Promise.all([
                  refreshSchedules(true),
                  Promise.resolve(fetchAcceptedJobs())
                ]).then(() => {
                  setScheduleUpdateSuccess('Schedule updated successfully!');
                  setTimeout(() => setScheduleUpdateSuccess(null), 3000);
                }).catch(() => {
                  setTimeout(() => setScheduleUpdateSuccess(null), 5000);
                });
              }
              
              setCustomerManagementRefreshTrigger(prev => prev + 1);
            }}
            defaultDate={customerScheduleDefaultDate}
            onEditSchedule={handleEditSchedule}
          />
        </>
      )}

      {/* Route Optimization Section */}
      {activeSection === 'route-optimization' && (
        <React.Suspense fallback={<CircularProgress />}>
          {user?._id || user?.id ? (
            <RouteOptimizationDashboard providerId={user?._id || user?.id || ''} />
          ) : (
            <Alert severity="error">Provider ID not found. Please refresh the page.</Alert>
          )}
        </React.Suspense>
      )}

      {/* Main Dashboard Content */}
      {activeSection === 'dashboard' && (
        <>
          {/* Insurance Reminder (hidden for shared interactive demo sandbox) */}
          {!(user as any)?.isDemo && (() => {
            const provider = user as any;
            return (!provider?.insuranceProvider || !provider?.insurancePolicyNumber || !provider?.insuranceExpiryDate || (provider?.insuranceExpiryDate && new Date(provider.insuranceExpiryDate) < new Date()));
          })() && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => window.location.href = '/profile'}
                >
                  Update Profile
                </Button>
              }
            >
              <strong>Insurance Required:</strong> {(() => {
                const provider = user as any;
                return !provider?.insuranceProvider || !provider?.insurancePolicyNumber || !provider?.insuranceExpiryDate 
                  ? "Complete your insurance information in your profile to submit bids on service requests."
                  : "Your insurance has expired. Update your insurance information to continue bidding.";
              })()}
            </Alert>
          )}


      <Grid container spacing={3}>
        {/* Available Requests */}
        <Grid item xs={12} id="available-requests">
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Available Service Requests
                <InfoTip title="Marketplace jobs you can bid on (after verification). Your existing customers live under Customer Management." />
              </Typography>
              <Button
                variant="outlined"
                onClick={fetchAvailableRequests}
              >
                Refresh
              </Button>
            </Box>

            {/* Marketplace verification — tools remain available without verification */}
            {user && (user as any).isVerified === false && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Marketplace Access Requires Verification
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  You can use business tools (CRM, scheduling, routes, Additional Services, renewals, Financial Dashboard)
                  right away. To view and bid on new marketplace service requests, upload the documents below and wait
                  for admin verification (typically 1–2 business days).
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Required for marketplace bidding:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 1, mt: 0 }}>
                  <li>
                    <Typography variant="body2">
                      <strong>Insurance:</strong> Valid liability insurance document, policy number, and expiry date
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      <strong>Pesticide License:</strong> Required only to bid on weed control/fertilizing marketplace requests (IDOA Commercial Pesticide Applicator License)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      <strong>Business Information:</strong> Business name, contact information, and service areas
                    </Typography>
                  </li>
                </Box>
                <Typography variant="body2">
                  Upload documents in Settings. You will receive a text and email once marketplace verification is complete.
                </Typography>
              </Alert>
            )}

            {/* Notification for current customers with open requests */}
            {(() => {
              const currentCustomerRequests = requests.filter((r: any) => r.isCurrentCustomer === true);
              
              if (currentCustomerRequests.length > 0 && !dismissedCurrentCustomerNotification) {
                return (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 2 }}
                    onClose={() => setDismissedCurrentCustomerNotification(true)}
                  >
                    <Typography variant="body2">
                      <strong>Current Customer Alert:</strong> You have {currentCustomerRequests.length} service request{currentCustomerRequests.length !== 1 ? 's' : ''} from {currentCustomerRequests.length === 1 ? 'a current customer' : 'current customers'}. These are shown at the top of the list with a "Current Customer" badge.
                    </Typography>
                  </Alert>
                );
              }
              return null;
            })()}


            {requests.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {user && (user as any).isVerified === false 
                    ? "Account Verification Required"
                    : "No available requests"
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user && (user as any).isVerified === false
                    ? "Complete account verification to view and bid on service requests. Verification typically takes 1-2 business days."
                    : "Check back later for new service requests in your area"
                  }
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell>Property Type</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Requested Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow 
                        key={request._id}
                        sx={{
                          backgroundColor: request.isCurrentCustomer ? 'action.selected' : 'inherit',
                          '&:hover': {
                            backgroundColor: request.isCurrentCustomer ? 'action.hover' : 'action.hover',
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                              {formatServiceTypeName(request.serviceType?.name)}
                            </Typography>
                            {request.isCurrentCustomer && (
                              <Chip 
                                label="Current Customer" 
                                color="primary" 
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {(request.serviceType?.category === 'mowing' || request.serviceType?.id === 'lawnMowingTrimming') && request.baggingRequired && (
                              <Chip 
                                label="Bagging Required" 
                                color="warning" 
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          {request.frequency && (
                            <Typography variant="caption" color="text.secondary">
                              Frequency: {request.frequency === 'weekly' ? 'Weekly' : 
                                          request.frequency === 'bi-weekly' ? 'Bi-Weekly' :
                                          request.frequency === 'monthly' ? 'Monthly' :
                                          request.frequency === 'per-instance' ? 'Per Instance' :
                                          request.frequency === 'seasonal-contract' ? 'Seasonal Contract' : 
                                          request.frequency}
                            </Typography>
                          )}
                          {request.description && 
                           request.description !== 'No special instructions' && 
                           !request.description.startsWith('Services requested:') && (
                            <Typography variant="body2" color="text.secondary">
                              {request.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatPropertyType(request.propertyType)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn sx={{ fontSize: 16 }} />
                            <Typography variant="body2">
                              {request.address?.street}, {request.address?.city}, {request.address?.state} {request.address?.zipCode}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {formatDate(request.requestedDate)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleBidSubmit(request._id)}
                              disabled={(() => {
                                const provider = user as any;
                                const hasInsurance = provider?.insuranceProvider && provider?.insurancePolicyNumber && provider?.insuranceExpiryDate;
                                const insuranceValid = hasInsurance && (!provider?.insuranceExpiryDate || new Date(provider.insuranceExpiryDate) >= new Date());
                                
                                // Check if this is a weed control service
                                const isWeedControl = request?.serviceType?.category === 'fertilizing' || 
                                                      request?.serviceType?.id === 'weedControlFertilizing' ||
                                                      (request?.serviceType?.name?.toLowerCase().includes('fertiliz')) ||
                                                      (request?.serviceType?.name?.toLowerCase().includes('weed'));
                                
                                const hasPesticideLicense = provider?.pesticideLicenseOnFile && provider?.pesticideLicenseDocumentUrl;
                                const pesticideLicenseValid = !isWeedControl || (hasPesticideLicense && (!provider?.pesticideLicenseExpiryDate || new Date(provider.pesticideLicenseExpiryDate) >= new Date()));
                                
                                return !insuranceValid || !pesticideLicenseValid;
                              })()}
                              title={(() => {
                                const provider = user as any;
                                if (!provider?.insuranceProvider || !provider?.insurancePolicyNumber || !provider?.insuranceExpiryDate) {
                                  return "Complete insurance information in your profile to submit bids";
                                } else if (provider?.insuranceExpiryDate && new Date(provider.insuranceExpiryDate) < new Date()) {
                                  return "Your insurance has expired. Update your insurance information to submit bids";
                                }
                                
                                // Check if this is a weed control service
                                const isWeedControl = request?.serviceType?.category === 'fertilizing' || 
                                                      request?.serviceType?.id === 'weedControlFertilizing' ||
                                                      (request?.serviceType?.name?.toLowerCase().includes('fertiliz')) ||
                                                      (request?.serviceType?.name?.toLowerCase().includes('weed'));
                                
                                if (isWeedControl) {
                                  if (!provider?.pesticideLicenseOnFile || !provider?.pesticideLicenseDocumentUrl) {
                                    return "Upload your IDOA Commercial Pesticide Applicator License in Settings to bid on weed control services";
                                  } else if (provider?.pesticideLicenseExpiryDate && new Date(provider.pesticideLicenseExpiryDate) < new Date()) {
                                    return "Your pesticide license has expired. Update your license information to submit bids";
                                  }
                                }
                                
                                return "Submit bid for this service request";
                              })()}
                            >
                              {(() => {
                                const provider = user as any;
                                if (!provider?.insuranceProvider || !provider?.insurancePolicyNumber || !provider?.insuranceExpiryDate) {
                                  return "Insurance Required";
                                } else if (provider?.insuranceExpiryDate && new Date(provider.insuranceExpiryDate) < new Date()) {
                                  return "Insurance Expired";
                                }
                                
                                // Check if this is a weed control service
                                const isWeedControl = request?.serviceType?.category === 'fertilizing' || 
                                                      request?.serviceType?.id === 'weedControlFertilizing' ||
                                                      (request?.serviceType?.name?.toLowerCase().includes('fertiliz')) ||
                                                      (request?.serviceType?.name?.toLowerCase().includes('weed'));
                                
                                if (isWeedControl) {
                                  if (!provider?.pesticideLicenseOnFile || !provider?.pesticideLicenseDocumentUrl) {
                                    return "License Required";
                                  } else if (provider?.pesticideLicenseExpiryDate && new Date(provider.pesticideLicenseExpiryDate) < new Date()) {
                                    return "License Expired";
                                  }
                                }
                                
                                return "Submit Bid";
                              })()}
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleViewRequestDetails(request)}
                              title="View Request Details"
                            >
                              <Visibility />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* My Bids */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              My Bids
            </Typography>
            <List>
              {(() => {
                // Separate expired and active bids
                const activeBids = bids.filter(bid => bid.status !== 'accepted' && bid.status !== 'paid' && bid.status !== 'expired');
                const expiredBids = bids.filter(bid => bid.status === 'expired');
                
                if (activeBids.length === 0 && expiredBids.length === 0) {
                  return (
                    <ListItem>
                      <ListItemText
                        primary="No pending bids"
                        secondary="Accepted bids are managed in your customer list and schedule"
                      />
                    </ListItem>
                  );
                }
                
                return (
                  <>
                    {/* Active Bids */}
                    {activeBids
                      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                      .map((bid) => (
                  <ListItem key={bid._id} divider>
                    <ListItemText
                      primary={
                        <Box>
                          {/* Customer Name - Most Prominent */}
                          <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                            {typeof bid.requestId !== 'string' && (bid.requestId as any)?.customerId ? 
                              `${(bid.requestId as any).customerId.firstName} ${(bid.requestId as any).customerId.lastName}` : 
                              'Customer Name Unavailable'
                            }
                          </Typography>
                          
                          {/* Address */}
                          <Typography 
                            variant="body1" 
                            color="text.primary" 
                            sx={{ 
                              cursor: 'pointer', 
                              textDecoration: 'underline',
                              '&:hover': { color: 'primary.dark' },
                              mb: 1
                            }}
                            onClick={() => {
                              if (typeof bid.requestId !== 'string' && (bid.requestId as any)?.address) {
                                const addr = (bid.requestId as any).address;
                                handleViewSatellite(`${addr.street}, ${addr.city}, ${addr.state} ${addr.zipCode}`);
                              }
                            }}
                          >
                            <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            {typeof bid.requestId !== 'string' && (bid.requestId as any)?.address ? 
                              `${(bid.requestId as any).address.street}, ${(bid.requestId as any).address.city}, ${(bid.requestId as any).address.state} ${(bid.requestId as any).address.zipCode}` : 
                              'Address unavailable'
                            }
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          {/* Property Type */}
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>Property Type:</strong> {typeof bid.requestId !== 'string' && (bid.requestId as any)?.propertyType ? 
                              (bid.requestId as any).propertyType : 'Not specified'}
                          </Typography>
                          
                          {/* Services Requested */}
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>Services:</strong> {typeof bid.requestId !== 'string' && (bid.requestId as any)?.serviceType?.name ? 
                              (bid.requestId as any).serviceType.name : 'Services not specified'}
                          </Typography>
                          
                          {/* Bid Submission Date */}
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Bid Submitted:</strong> {new Date(bid.createdAt).toLocaleDateString()}
                          </Typography>
                          
                          {/* Status Chip */}
                          <Chip
                            label={bid.status?.charAt(0).toUpperCase() + bid.status?.slice(1)}
                            color={getStatusColor(bid.status) as any}
                            size="small"
                          />
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <IconButton
                          edge="end"
                          onClick={() =>
                            setBidDetailsDialog({ open: true, bidId: String(bid._id || bid.id) })
                          }
                          title="View Details"
                        >
                          <Visibility />
                        </IconButton>
                        {(bid.status === 'pending' || bid.status === 'rejected') && bid._id && (
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveBid(bid._id!)}
                            title="Delete Bid"
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                      ))}
                      
                    {/* Expired Bids Section */}
                    {expiredBids.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="error.main" sx={{ px: 2, mb: 1, fontWeight: 'bold' }}>
                          Expired Bids ({expiredBids.length})
                        </Typography>
                        {expiredBids
                          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                          .map((bid) => (
                            <ListItem key={bid._id} divider sx={{ opacity: 0.8, bgcolor: 'error.50' }}>
                              <ListItemText
                                primary={
                                  <Box>
                                    <Typography variant="h6" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                                      {typeof bid.requestId !== 'string' && (bid.requestId as any)?.customerId ? 
                                        `${(bid.requestId as any).customerId.firstName} ${(bid.requestId as any).customerId.lastName}` : 
                                        'Customer Name Unavailable'}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                      <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                      {typeof bid.requestId !== 'string' && (bid.requestId as any)?.address ? 
                                        `${(bid.requestId as any).address.street}, ${(bid.requestId as any).address.city}` : 
                                        'Address unavailable'}
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Chip
                                      label="EXPIRED"
                                      color="error"
                                      size="small"
                                      sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Expired on {new Date(bid.createdAt).toLocaleDateString()} • No longer available for customers to accept
                                    </Typography>
                                  </Box>
                                }
                              />
                              <ListItemSecondaryAction>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <IconButton
                                    edge="end"
                                    onClick={() =>
                                      setBidDetailsDialog({ open: true, bidId: String(bid._id || bid.id) })
                                    }
                                    title="View Details"
                                  >
                                    <Visibility />
                                  </IconButton>
                                  {bid._id && (
                                    <IconButton
                                      edge="end"
                                      onClick={() => handleRemoveBid(bid._id!)}
                                      title="Delete Expired Bid"
                                      color="error"
                                    >
                                      <Delete />
                                    </IconButton>
                                  )}
                                </Box>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                      </>
                    )}
                  </>
                );
              })()}
            </List>
          </Paper>
        </Grid>


        {/* Accepted Jobs Needing Scheduling */}
        {acceptedJobs.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Accepted Jobs - Schedule Required
                <InfoTip title="Won marketplace jobs that still need a calendar date — use Schedule or Add to Today." />
              </Typography>
              {/* Per-instance notification */}
              {(() => {
                const shouldShow = acceptedJobs.some(job => {
                  const jobId = (job._id || job.id || '').toString();
                  const isPerInstance = isPerInstanceJob(job);
                  const isDismissed = dismissedPerInstanceNotifications.has(jobId);
                  return isPerInstance && !isDismissed;
                });
                return shouldShow;
              })() && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 2 }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        // Dismiss all per-instance notifications
                        acceptedJobs.forEach(job => {
                          if (isPerInstanceJob(job)) {
                            const jobId = (job._id || job.id || '').toString();
                            dismissPerInstanceNotification(jobId);
                          }
                        });
                      }}
                    >
                      <Close fontSize="inherit" />
                    </IconButton>
                  }
                >
                  <Typography variant="body2">
                    <strong>Note:</strong> Some of these jobs are <strong>Per Instance</strong> services. 
                    These services don't require initial scheduling - you can schedule them on-demand when needed 
                    (e.g., snow removal when it snows). You can still schedule them now if you prefer.
                  </Typography>
                </Alert>
              )}
              <List>
                {acceptedJobs.map((job) => (
                  <ListItem key={job._id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">
                            {/* Show only the service that matches this ServiceRequest's serviceType */}
                            {(() => {
                              // Map serviceType category to servicePricing key
                              const serviceCategory = job.serviceType?.category;
                              let matchingServiceKey: string | null = null;
                              if (serviceCategory === 'mowing') {
                                matchingServiceKey = 'lawnMowingTrimming';
                              } else if (serviceCategory === 'fertilizing') {
                                matchingServiceKey = 'weedControlFertilizing';
                              } else if (serviceCategory === 'snow_removal') {
                                matchingServiceKey = 'snowRemoval';
                              }
                              
                              // Find matching service in servicePricing array
                              if (job.acceptedBid?.servicePricing && matchingServiceKey) {
                                const matchingService = job.acceptedBid.servicePricing.find((sp: any) => 
                                  sp.service === matchingServiceKey || 
                                  sp.serviceName?.toLowerCase().includes(serviceCategory || '')
                                );
                                if (matchingService) {
                                  return normalizeServiceName(matchingService.serviceName || matchingService.service);
                                }
                              }
                              
                              // Fallback to ServiceRequest's serviceType name
                              return formatServiceTypeName(job.serviceType?.name);
                            })()}
                          </Typography>
                          <Chip label="Needs Scheduling" color="warning" size="small" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {job.address?.street}, {job.address?.city}, {job.address?.state}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            Customer: {job.customerId?.firstName} {job.customerId?.lastName}
                          </Typography>
                          {/* Show only the service pricing that matches this ServiceRequest's serviceType */}
                          {(() => {
                            // Map serviceType category to servicePricing key
                            const serviceCategory = job.serviceType?.category;
                            let matchingServiceKey: string | null = null;
                            if (serviceCategory === 'mowing') {
                              matchingServiceKey = 'lawnMowingTrimming';
                            } else if (serviceCategory === 'fertilizing') {
                              matchingServiceKey = 'weedControlFertilizing';
                            } else if (serviceCategory === 'snow_removal') {
                              matchingServiceKey = 'snowRemoval';
                            }
                            
                            // Find matching service in servicePricing array
                            if (job.acceptedBid?.servicePricing && matchingServiceKey) {
                              const matchingService = job.acceptedBid.servicePricing.find((sp: any) => 
                                sp.service === matchingServiceKey || 
                                sp.serviceName?.toLowerCase().includes(serviceCategory || '')
                              );
                              if (matchingService) {
                                // For seasonal contracts, check the actual serviceFrequency
                                let displayFrequency = matchingService.frequency;
                                if (matchingService.frequency === 'seasonal-contract' && matchingService.seasonalContractDetails?.serviceFrequency) {
                                  displayFrequency = matchingService.seasonalContractDetails.serviceFrequency;
                                }
                                
                                // Format frequency for display
                                const frequencyLabel = displayFrequency === 'per-instance' ? 'Per Instance' :
                                                      displayFrequency === 'weekly' ? 'Weekly' :
                                                      displayFrequency === 'bi-weekly' ? 'Bi-Weekly' :
                                                      displayFrequency === 'monthly' ? 'Monthly' :
                                                      displayFrequency === 'seasonal-contract' ? 'Seasonal Contract' :
                                                      displayFrequency;
                                
                                return (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {normalizeServiceName(matchingService.serviceName || matchingService.service)}: {formatCurrency(matchingService.price)} ({frequencyLabel})
                                    </Typography>
                                  </Box>
                                );
                              }
                            }
                            return null;
                          })()}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {isPerInstanceJob(job) && (
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={async () => {
                              try {
                                const response = await apiService.post(`/service-requests/${job._id}/dismiss-schedule`);
                                if (response.success) {
                                  // Refresh the accepted jobs list
                                  await fetchAcceptedJobs();
                                } else {
                                  console.error('Failed to dismiss service:', response.error);
                                }
                              } catch (err: any) {
                                console.error('Failed to dismiss service:', err);
                              }
                            }}
                          >
                            Schedule Later as Needed
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          startIcon={<Schedule />}
                          onClick={() => handleScheduleJob(job)}
                        >
                          Schedule
                        </Button>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Today's Jobs */}
        <Grid item xs={12} md={6} data-tour="demo-todays-jobs">
          <TodaysJobs
            todaysJobs={todaysJobs}
            onCompleteJob={handleCompleteJob}
            onSkipJob={handleSkipJob}
            onRetryPayment={handleRetryPayment}
            paymentStatuses={paymentStatuses}
            retryingPayment={retryingPayment}
            formatServiceTypeName={formatServiceTypeName}
            getStatusColor={getStatusColor}
          />
        </Grid>

        {/* Weekly Schedule */}
        <Grid item xs={12} md={6} data-tour="demo-this-weeks-schedule">
          <ThisWeeksSchedule
            scheduledJobs={scheduledJobs}
            onEditSchedule={handleEditSchedule}
            formatServiceTypeName={formatServiceTypeName}
          />
        </Grid>

        {/* Payments Data - Matching My Earnings Page */}
        {earningsData && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Payments
              </Typography>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle color="success" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Next Payout
                        </Typography>
                      </Box>
                      <Typography variant="h5" color="success.main">
                        {formatCurrency(earningsData.summary?.nextPayoutAmount || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Weekly on Fridays
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <HourglassEmpty color="warning" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Processing
                        </Typography>
                      </Box>
                      <Typography variant="h5" color="warning.main">
                        {formatCurrency(earningsData.processing?.totalEarnings || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {earningsData.processing?.totalJobs || 0} jobs settling
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Schedule color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Pending Total
                        </Typography>
                      </Box>
                      <Typography variant="h5">
                        {formatCurrency(earningsData.summary?.pendingEarnings || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ready + Processing
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TrendingUp color="success" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Lifetime Earnings
                        </Typography>
                      </Box>
                      <Typography variant="h5">
                        {formatCurrency(earningsData.summary?.lifetimeEarnings || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {earningsData.summary?.totalPayouts || 0} payouts
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Payment History */}
        {payments.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Recent Payments
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Your Earnings</TableCell>
                      <TableCell>Platform Fee</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.slice(0, 5).map((payment) => (
                      <TableRow key={payment._id}>
                        <TableCell>
                          {new Date(payment.completedAt || payment.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <span>
                              {(() => {
                                // Priority 1: Use the stored serviceType from the payment record (this is the actual service performed)
                                if (payment.serviceType) {
                                  const categoryMap: { [key: string]: string } = {
                                    'mowing': 'Lawn Mowing/Trimming',
                                    'fertilizing': 'Weed Control/Fertilizing',
                                    'snow_removal': 'Snow Removal',
                                    'other': payment.serviceType.name || 'Other Service'
                                  };
                                  return categoryMap[payment.serviceType.category] || payment.serviceType.name || 'Service';
                                }
                                
                                // Priority 2: Fallback to serviceRequestId.serviceType (the specific service that was completed)
                                if (payment.serviceRequestId?.serviceType) {
                                  const categoryMap: { [key: string]: string } = {
                                    'mowing': 'Lawn Mowing/Trimming',
                                    'fertilizing': 'Weed Control/Fertilizing',
                                    'snow_removal': 'Snow Removal',
                                    'other': payment.serviceRequestId.serviceType.name || 'Other Service'
                                  };
                                  return categoryMap[payment.serviceRequestId.serviceType.category] || payment.serviceRequestId.serviceType.name || 'Service';
                                }
                                
                                // Priority 3: Check onboardedServices (from backend for onboarded customers without bids)
                                if (payment.onboardedServices && Array.isArray(payment.onboardedServices) && payment.onboardedServices.length > 0) {
                                  return payment.onboardedServices
                                    .map((name: string) => normalizeServiceName(name))
                                    .filter((name: string, index: number, arr: string[]) => {
                                      // Remove duplicates
                                      return arr.indexOf(name) === index;
                                    })
                                    .join(', ');
                                }
                                
                                // Final fallback: Use serviceRequestId.serviceType.name
                                return payment.serviceRequestId?.serviceType?.name
                                  ?.split(', ')
                                  .map((s: string) => 
                                    s === 'mowing' || s === 'edging' ? 'Lawn Mowing/Trimming' :
                                    s === 'fertilizingWeedControl' ? 'Fertilizing/Weed Control' :
                                    s.charAt(0).toUpperCase() + s.slice(1)
                                  )
                                  .filter((name: string, index: number, arr: string[]) => {
                                    // Remove duplicates (e.g., if both 'mowing' and 'edging' are present, they both map to 'Lawn Mowing/Trimming')
                                    return arr.indexOf(name) === index;
                                  })
                                  .join(', ') || 'Service';
                              })()}
                            </span>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {payment.customerId?.firstName} {payment.customerId?.lastName}
                        </TableCell>
                        <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          ${payment.providerCut?.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          ${payment.platformCut?.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              payment.type === 'seasonal_contract_subscription_payment'
                                ? 'Monthly (seasonal)'
                                : payment.status === 'skip' && payment.type === 'seasonal_contract_subscription_skip'
                                  ? 'Monthly Payment Plan'
                                  : payment.status
                            }
                            color={
                              payment.type === 'seasonal_contract_subscription_payment'
                                ? 'default'
                                : payment.status === 'skip' && payment.type === 'seasonal_contract_subscription_skip'
                                  ? 'info'
                                  : payment.status === 'completed'
                                  ? 'success'
                                  : 'warning'
                            }
                            size="small"
                            sx={
                              payment.type === 'seasonal_contract_subscription_payment'
                                ? { backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }
                                : undefined
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Bid Submission Dialog */}
        <Dialog
        open={bidDialog.open}
        onClose={() => {
          setBidDialog({ open: false, requestId: '', requestData: null });
          setIsCustomerOnboardedByThisProvider(false);
          setBidFieldErrors({});
          setBidError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit Bid</DialogTitle>
        <DialogContent>
          {bidDialog.requestData && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Service Request Details:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Services:</strong> {formatServiceTypeName(bidDialog.requestData.serviceType?.name)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Location:</strong> {bidDialog.requestData.address?.street}, {bidDialog.requestData.address?.city}, {bidDialog.requestData.address?.state} {bidDialog.requestData.address?.zipCode}
              </Typography>

              {/* Property View Links */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Property View:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const address = `${bidDialog.requestData.address?.street}, ${bidDialog.requestData.address?.city}, ${bidDialog.requestData.address?.state} ${bidDialog.requestData.address?.zipCode}`;
                      setSatelliteDialog({ open: true, address });
                    }}
                  >
                    🛰️ Satellite View
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const address = `${bidDialog.requestData.address?.street}, ${bidDialog.requestData.address?.city}, ${bidDialog.requestData.address?.state} ${bidDialog.requestData.address?.zipCode}`;
                      window.open(`https://www.google.com/maps?q=${encodeURIComponent(address)}`, '_blank');
                    }}
                  >
                    🗺️ Street View
                  </Button>
                  {['single_residential', 'vacant_lot'].includes(bidDialog.requestData?.propertyType) && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const addr = `${bidDialog.requestData.address?.street}, ${bidDialog.requestData.address?.city}, ${bidDialog.requestData.address?.state} ${bidDialog.requestData.address?.zipCode}`;
                        setMeasurementsDialog({ open: true, serviceRequestId: bidDialog.requestId, propertyType: bidDialog.requestData?.propertyType || '', address: addr, initialData: bidDialog.requestData?.deepLawnData ?? null });
                      }}
                    >
                      📐 Measurements
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Click to open Google Maps in a new tab to assess property size and layout
                </Typography>
              </Box>

              {/* Customer property documents (Multi-Family, Commercial, HOA) */}
              {Array.isArray(bidDialog.requestData?.customerAttachments) && bidDialog.requestData.customerAttachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Customer property documents
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Plot surveys, photos, or other documents uploaded by the customer to help with your bid.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {bidDialog.requestData.customerAttachments.map((att: any, i: number) => (
                      <Button
                        key={i}
                        size="small"
                        variant="outlined"
                        href={att.viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {att.name || `Document ${i + 1}`}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {bidError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bidError}
            </Alert>
          )}

          {/* Service Pricing Configuration */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Service Schedule & Pricing
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Set the price for each service. Frequency defaults to what the customer requested. Duration is optional.
            </Typography>
            
            {bidForm.servicePricing.map((servicePricing, index) => {
              const durationError = bidFieldErrors[`servicePricing.${index}.duration`];
              const frequencyError = bidFieldErrors[`servicePricing.${index}.frequency`];
              const priceFieldKey =
                servicePricing.frequency === 'seasonal-contract'
                  ? `servicePricing.${index}.seasonalContractDetails.totalContractPrice`
                  : `servicePricing.${index}.price`;
              const priceError = bidFieldErrors[priceFieldKey];
              const contractStartError =
                bidFieldErrors[`servicePricing.${index}.seasonalContractDetails.contractStartDate`];
              const contractEndError =
                bidFieldErrors[`servicePricing.${index}.seasonalContractDetails.contractEndDate`];
              const contractServiceFrequencyError =
                bidFieldErrors[`servicePricing.${index}.seasonalContractDetails.serviceFrequency`];

              return (
                <Box key={index} sx={{ mb: 3, p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {normalizeServiceName(servicePricing.serviceName)}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      {servicePricing.frequency === 'seasonal-contract' ? (
                        <TextField
                          fullWidth
                          size="small"
                          label="Total Contract Price ($)"
                          type="text"
                          value={servicePricing.seasonalContractDetails?.totalContractPrice || ''}
                          onChange={(e) => {
                            // Allow only numbers and decimal point
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const sanitizedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                            const newPricing = [...bidForm.servicePricing];
                            if (!newPricing[index].seasonalContractDetails) {
                              newPricing[index].seasonalContractDetails = {
                                contractStartDate: '',
                                contractEndDate: '',
                                totalContractPrice: '',
                                serviceFrequency: 'weekly',
                              };
                            }
                            newPricing[index].seasonalContractDetails!.totalContractPrice = sanitizedValue;
                            setBidForm({ ...bidForm, servicePricing: newPricing });
                          }}
                          inputProps={{ 
                            inputMode: 'decimal',
                            pattern: '[0-9]*\\.?[0-9]*',
                            style: { 
                              MozAppearance: 'textfield',
                              WebkitAppearance: 'none',
                            }
                          }}
                          sx={{
                            '& input[type=text]::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                            '& input[type=text]::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                            '& input[type=text]': {
                              MozAppearance: 'textfield',
                            },
                          }}
                          required
                          error={Boolean(priceError)}
                          helperText={priceError}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          label="Price ($)"
                          type="text"
                          value={servicePricing.price}
                          onChange={(e) => {
                            // Allow only numbers and decimal point
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const sanitizedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                            const newPricing = [...bidForm.servicePricing];
                            newPricing[index].price = sanitizedValue;
                            setBidForm({ ...bidForm, servicePricing: newPricing });
                          }}
                          inputProps={{ 
                            inputMode: 'decimal',
                            pattern: '[0-9]*\\.?[0-9]*',
                            style: { 
                              MozAppearance: 'textfield',
                              WebkitAppearance: 'none',
                            }
                          }}
                          sx={{
                            '& input[type=text]::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                            '& input[type=text]::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                            '& input[type=text]': {
                              MozAppearance: 'textfield',
                            },
                          }}
                          required
                          error={Boolean(priceError)}
                          helperText={priceError}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Duration (hours) — optional"
                        type="number"
                        value={servicePricing.duration}
                        onChange={(e) => {
                          const newPricing = [...bidForm.servicePricing];
                          newPricing[index].duration = e.target.value;
                          setBidForm({ ...bidForm, servicePricing: newPricing });
                        }}
                        inputProps={{ min: 0, step: 0.25 }}
                        error={Boolean(durationError)}
                        helperText={durationError || 'Optional · 15 min increments if set'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" error={Boolean(frequencyError)}>
                        <InputLabel>Frequency</InputLabel>
                        <Select
                          value={servicePricing.frequency}
                          onChange={(e) => {
                            const newPricing = [...bidForm.servicePricing];
                            newPricing[index].frequency = e.target.value;
                            newPricing[index].isSeasonalContract = e.target.value === 'seasonal-contract';
                            // If changing FROM seasonal-contract, preserve seasonalContractDetails in case user changes back
                            // If changing TO seasonal-contract, initialize seasonalContractDetails if it doesn't exist
                            if (e.target.value === 'seasonal-contract' && !newPricing[index].seasonalContractDetails) {
                              newPricing[index].seasonalContractDetails = {
                                contractStartDate: '',
                                contractEndDate: '',
                                totalContractPrice: '',
                                serviceFrequency: 'weekly',
                              };
                            }
                            setBidForm({ ...bidForm, servicePricing: newPricing });
                          }}
                          label="Frequency"
                        >
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="bi-weekly">Bi-Weekly (Every 2 weeks)</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="per-instance">Per Instance (as needed)</MenuItem>
                          <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                        </Select>
                        <FormHelperText>
                          {frequencyError || 'Defaults to the customer\'s requested frequency'}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    {/* Seasonal Contract Details */}
                    {servicePricing.frequency === 'seasonal-contract' && (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Seasonal Contract Details
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Contract Start Date"
                                type="date"
                                value={servicePricing.seasonalContractDetails?.contractStartDate || ''}
                                onChange={(e) => {
                                  const newPricing = [...bidForm.servicePricing];
                                    if (!newPricing[index].seasonalContractDetails) {
                                      newPricing[index].seasonalContractDetails = {
                                        contractStartDate: '',
                                        contractEndDate: '',
                                        totalContractPrice: '',
                                        serviceFrequency: 'weekly',
                                        paymentSchedule: 'single-payment', // Default to single-payment
                                      };
                                    }
                                  newPricing[index].seasonalContractDetails!.contractStartDate = e.target.value;
                                  setBidForm({ ...bidForm, servicePricing: newPricing });
                                }}
                                InputLabelProps={{ shrink: true }}
                                required
                                error={Boolean(contractStartError)}
                                helperText={contractStartError}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Contract End Date"
                                type="date"
                                value={servicePricing.seasonalContractDetails?.contractEndDate || ''}
                                onChange={(e) => {
                                  const newPricing = [...bidForm.servicePricing];
                                    if (!newPricing[index].seasonalContractDetails) {
                                      newPricing[index].seasonalContractDetails = {
                                        contractStartDate: '',
                                        contractEndDate: '',
                                        totalContractPrice: '',
                                        serviceFrequency: 'weekly',
                                        paymentSchedule: 'single-payment', // Default to single-payment
                                      };
                                    }
                                  newPricing[index].seasonalContractDetails!.contractEndDate = e.target.value;
                                  setBidForm({ ...bidForm, servicePricing: newPricing });
                                }}
                                InputLabelProps={{ shrink: true }}
                                required
                                error={Boolean(contractEndError)}
                                helperText={contractEndError}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Service Frequency</InputLabel>
                                <Select
                                  value={servicePricing.seasonalContractDetails?.serviceFrequency || 'weekly'}
                                  onChange={(e) => {
                                    const newPricing = [...bidForm.servicePricing];
                                    // Preserve existing seasonalContractDetails or create new one with defaults
                                    const existingDetails = newPricing[index].seasonalContractDetails;
                                    newPricing[index].seasonalContractDetails = {
                                      contractStartDate: existingDetails?.contractStartDate || '',
                                      contractEndDate: existingDetails?.contractEndDate || '',
                                      totalContractPrice: existingDetails?.totalContractPrice || '',
                                      serviceFrequency: e.target.value as 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance',
                                      paymentSchedule: existingDetails?.paymentSchedule || 'single-payment', // Preserve payment schedule
                                    };
                                    setBidForm({ ...bidForm, servicePricing: newPricing });
                                  }}
                                  label="Service Frequency"
                                >
                                  <MenuItem value="weekly">Weekly</MenuItem>
                                  <MenuItem value="bi-weekly">Bi-Weekly (Every 2 weeks)</MenuItem>
                                  <MenuItem value="monthly">Monthly</MenuItem>
                                  <MenuItem value="per-instance">Per Instance</MenuItem>
                                </Select>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {servicePricing.seasonalContractDetails?.serviceFrequency === 'per-instance' 
                                    ? 'Services will be scheduled as needed (e.g., when snow falls). Payment is made after the first service.'
                                    : 'How often services will be performed during the contract period'}
                                </Typography>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Payment Schedule</InputLabel>
                                <Select
                                  value={servicePricing.seasonalContractDetails?.paymentSchedule || 'single-payment'}
                                  onChange={(e) => {
                                    const newPricing = [...bidForm.servicePricing];
                                    const existingDetails = newPricing[index].seasonalContractDetails;
                                    newPricing[index].seasonalContractDetails = {
                                      contractStartDate: existingDetails?.contractStartDate || '',
                                      contractEndDate: existingDetails?.contractEndDate || '',
                                      totalContractPrice: existingDetails?.totalContractPrice || '',
                                      serviceFrequency: existingDetails?.serviceFrequency || 'weekly',
                                      paymentSchedule: e.target.value as 'single-payment' | 'monthly',
                                    };
                                    setBidForm({ ...bidForm, servicePricing: newPricing });
                                  }}
                                  label="Payment Schedule"
                                >
                                  <MenuItem value="single-payment">One-Time Payment (Full payment after first service)</MenuItem>
                                  <MenuItem value="monthly">Monthly Payments (Charged on 1st of each month)</MenuItem>
                                </Select>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {servicePricing.seasonalContractDetails?.paymentSchedule === 'single-payment' 
                                    ? 'Customer will be charged the full amount after the first service.'
                                    : 'Customer will be charged monthly installments automatically.'}
                                  {bidDialog.requestData?.paymentSchedule && bidDialog.requestData.paymentSchedule !== servicePricing.seasonalContractDetails?.paymentSchedule && (
                                    <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'info.main', fontStyle: 'italic' }}>
                                      Customer requested: {bidDialog.requestData.paymentSchedule === 'monthly' ? 'Monthly Payments' : 'One-Time Payment'}
                                    </Box>
                                  )}
                                </Typography>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              );
            })}

            {/* Bid Summary - Per Service Breakdown */}
            {bidForm.servicePricing.some(sp => {
              if (sp.frequency === 'seasonal-contract') {
                return sp.seasonalContractDetails?.totalContractPrice;
              }
              return sp.price;
            }) && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'primary.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Bid Summary
                </Typography>
                {bidForm.servicePricing
                  .filter(sp => {
                    if (sp.frequency === 'seasonal-contract') {
                      return sp.seasonalContractDetails?.totalContractPrice;
                    }
                    return sp.price;
                  })
                  .map((service, index) => {
                    const price = service.frequency === 'seasonal-contract' && service.seasonalContractDetails?.totalContractPrice
                      ? parseFloat(service.seasonalContractDetails.totalContractPrice) || 0
                      : parseFloat(service.price) || 0;
                    const duration = parseFloat(service.duration) || 0;
                    const platformFeePercent = isCustomerOnboardedByThisProvider ? 1 : 5;
                    const extraRows = [
                      { label: 'Frequency:', value: service.frequency },
                      ...(duration > 0
                        ? [{ label: 'Duration:', value: `${Number(duration).toFixed(2)} hours` }]
                        : []),
                    ];

                    return (
                      <ProviderPriceBreakdown
                        key={index}
                        label={normalizeServiceName(service.serviceName)}
                        servicePrice={price}
                        platformFeePercent={platformFeePercent}
                        extraRows={extraRows}
                        {...salesTaxOptionsForJob(
                          (user as any)?.salesTaxRates,
                          bidDialog.requestData?.address,
                          bidDialog.requestData?.propertyType
                        )}
                      />
                    );
                  })}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Customer will see individual service pricing and can select different providers for each service
                </Typography>
              </Box>
            )}
          </Box>

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Additional Notes (Optional)"
            name="description"
            value={bidForm.description}
            onChange={handleBidFormChange}
            margin="normal"
            placeholder="Any additional details about your services, experience, or what's included..."
          error={Boolean(bidFieldErrors.description)}
          helperText={bidFieldErrors.description}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBidDialog({ open: false, requestId: '', requestData: null });
              setBidFieldErrors({});
              setBidError('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={submitBid} variant="contained" disabled={bidLoading}>
            {bidLoading ? 'Submitting...' : 'Submit Bid'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      {/* Canonical Bid Details (same as Reviews & Bids) */}
      <ProviderBidDetailsDialog
        open={bidDetailsDialog.open}
        bidId={bidDetailsDialog.bidId}
        onClose={() => setBidDetailsDialog({ open: false, bidId: null })}
      />

      {/* Satellite View Dialog */}
      <SatelliteViewDialog
        open={satelliteDialog.open}
        onClose={() => setSatelliteDialog({ open: false, address: '' })}
        address={satelliteDialog.address}
        title="Property Location View"
      />

      {/* DeepLawn Measurements Dialog */}
      <DeepLawnMeasurementsDialog
        open={measurementsDialog.open}
        onClose={() => setMeasurementsDialog(prev => ({ ...prev, open: false }))}
        serviceRequestId={measurementsDialog.serviceRequestId}
        propertyType={measurementsDialog.propertyType}
        address={measurementsDialog.address}
        initialData={measurementsDialog.initialData}
      />

      {/* Service Request Details Dialog */}
      <Dialog
        open={requestDetailsDialog.open}
        onClose={() => setRequestDetailsDialog({ open: false, request: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Service Request Details</DialogTitle>
        <DialogContent>
          {requestDetailsDialog.request && (
            <Box>
              {/* Service Type */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Service Requested
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="body1">
                  {formatServiceTypeName(requestDetailsDialog.request.serviceType?.name)}
                </Typography>
                {(requestDetailsDialog.request.serviceType?.category === 'mowing' || requestDetailsDialog.request.serviceType?.id === 'lawnMowingTrimming') && requestDetailsDialog.request.baggingRequired && (
                  <Chip label="Bagging Required" color="warning" size="small" />
                )}
              </Box>

              {/* Frequency */}
              {requestDetailsDialog.request.frequency && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Frequency
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {requestDetailsDialog.request.frequency === 'weekly' ? 'Weekly' : 
                     requestDetailsDialog.request.frequency === 'bi-weekly' ? 'Bi-Weekly' :
                     requestDetailsDialog.request.frequency === 'monthly' ? 'Monthly' :
                     requestDetailsDialog.request.frequency === 'per-instance' ? 'Per Instance' :
                     requestDetailsDialog.request.frequency === 'seasonal-contract' ? 'Seasonal Contract' : 
                     requestDetailsDialog.request.frequency}
                  </Typography>
                </>
              )}

              {/* Address */}
              <Typography variant="h6" gutterBottom>
                Property Address
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {requestDetailsDialog.request.address?.street}, {requestDetailsDialog.request.address?.city}, {requestDetailsDialog.request.address?.state} {requestDetailsDialog.request.address?.zipCode}
              </Typography>

              {/* Requested Date */}
              <Typography variant="h6" gutterBottom>
                Requested Date
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatDate(requestDetailsDialog.request.requestedDate)}
              </Typography>

              {/* Special Instructions */}
              {requestDetailsDialog.request.description && 
               requestDetailsDialog.request.description !== 'No special instructions' && 
               !requestDetailsDialog.request.description.startsWith('Services requested:') && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Special Instructions
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {requestDetailsDialog.request.description}
                  </Typography>
                </>
              )}

              {/* Status */}
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
              <Chip
                label={requestDetailsDialog.request.status?.charAt(0).toUpperCase() + requestDetailsDialog.request.status?.slice(1)}
                color={getStatusColor(requestDetailsDialog.request.status) as any}
                sx={{ mb: 2 }}
              />

              {/* Property View Links */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Property Assessment
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    const address = `${requestDetailsDialog.request.address?.street}, ${requestDetailsDialog.request.address?.city}, ${requestDetailsDialog.request.address?.state} ${requestDetailsDialog.request.address?.zipCode}`;
                    setSatelliteDialog({ open: true, address });
                  }}
                >
                  🛰️ Satellite View
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const address = `${requestDetailsDialog.request.address?.street}, ${requestDetailsDialog.request.address?.city}, ${requestDetailsDialog.request.address?.state} ${requestDetailsDialog.request.address?.zipCode}`;
                    window.open(`https://www.google.com/maps?q=${encodeURIComponent(address)}`, '_blank');
                  }}
                >
                  🗺️ Street View
                </Button>
                {['single_residential', 'vacant_lot'].includes(requestDetailsDialog.request?.propertyType) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const addr = `${requestDetailsDialog.request.address?.street}, ${requestDetailsDialog.request.address?.city}, ${requestDetailsDialog.request.address?.state} ${requestDetailsDialog.request.address?.zipCode}`;
                      setMeasurementsDialog({ open: true, serviceRequestId: requestDetailsDialog.request._id, propertyType: requestDetailsDialog.request.propertyType || '', address: addr, initialData: requestDetailsDialog.request.deepLawnData ?? null });
                    }}
                  >
                    📐 Measurements
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Use these links to assess the property size and layout for accurate bidding
              </Typography>

              {/* Customer property documents (Multi-Family, Commercial, HOA) */}
              {Array.isArray(requestDetailsDialog.request?.customerAttachments) && requestDetailsDialog.request.customerAttachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Customer property documents
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Plot surveys, photos, or other documents uploaded by the customer.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {requestDetailsDialog.request.customerAttachments.map((att: any, i: number) => (
                      <Button
                        key={i}
                        size="small"
                        variant="outlined"
                        href={att.viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {att.name || `Document ${i + 1}`}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDetailsDialog({ open: false, request: null })}>
            Close
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setRequestDetailsDialog({ open: false, request: null });
              handleBidSubmit(requestDetailsDialog.request?._id);
            }}
          >
            Submit Bid
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Job Dialog */}
      <Dialog 
        open={scheduleDialog.open} 
        onClose={() => {
          setScheduleDialog({ open: false, job: null });
          setScheduleForm({ scheduledDate: '', estimatedDuration: '' });
          setScheduleError(null);
          setScheduleLoading(false);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Schedule Job</DialogTitle>
        <DialogContent>
          {scheduleDialog.job && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Service: {formatServiceTypeName(scheduleDialog.job.serviceType?.name)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customer: {scheduleDialog.job.customerId?.firstName} {scheduleDialog.job.customerId?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Address: {scheduleDialog.job.address?.street}, {scheduleDialog.job.address?.city}
              </Typography>
              {scheduleDialog.job.acceptedBid?.servicePricing && (() => {
                // Get the matching service for this job's serviceType
                const serviceCategory = scheduleDialog.job.serviceType?.category;
                let matchingServiceKey: string | null = null;
                if (serviceCategory === 'mowing') {
                  matchingServiceKey = 'lawnMowingTrimming';
                } else if (serviceCategory === 'fertilizing') {
                  matchingServiceKey = 'weedControlFertilizing';
                } else if (serviceCategory === 'snow_removal') {
                  matchingServiceKey = 'snowRemoval';
                }
                
                const matchingService = matchingServiceKey 
                  ? scheduleDialog.job.acceptedBid.servicePricing.find((sp: any) => 
                      sp.service === matchingServiceKey || 
                      sp.serviceName?.toLowerCase().includes(serviceCategory || '')
                    )
                  : null;
                
                if (matchingService) {
                  // For seasonal contracts, check the actual serviceFrequency
                  let displayFrequency = matchingService.frequency;
                  if (matchingService.frequency === 'seasonal-contract' && matchingService.seasonalContractDetails?.serviceFrequency) {
                    displayFrequency = matchingService.seasonalContractDetails.serviceFrequency;
                  }
                  
                  // Format frequency for display
                  const frequencyLabel = displayFrequency === 'per-instance' ? 'Per Instance' :
                                        displayFrequency === 'weekly' ? 'Weekly' :
                                        displayFrequency === 'bi-weekly' ? 'Bi-Weekly' :
                                        displayFrequency === 'monthly' ? 'Monthly' :
                                        displayFrequency === 'seasonal-contract' ? 'Seasonal Contract' :
                                        displayFrequency;
                  
                  // Show contract dates for seasonal contracts
                  const contractStartDate = matchingService.seasonalContractDetails?.contractStartDate;
                  const contractEndDate = matchingService.seasonalContractDetails?.contractEndDate;
                  
                  return (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="primary">
                        <strong>Frequency:</strong> {frequencyLabel}
                      </Typography>
                      {matchingService.frequency === 'seasonal-contract' && contractStartDate && contractEndDate && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          <strong>Contract Period:</strong> {formatContractDateUTC(contractStartDate)} - {formatContractDateUTC(contractEndDate)}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                
                // Fallback: show all frequencies
                const fallbackServices = scheduleDialog.job.acceptedBid.servicePricing;
                const seasonalService = fallbackServices.find((sp: any) => sp.frequency === 'seasonal-contract');
                const contractStartDate = seasonalService?.seasonalContractDetails?.contractStartDate;
                const contractEndDate = seasonalService?.seasonalContractDetails?.contractEndDate;
                
                return (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="primary">
                      <strong>Frequency:</strong> {fallbackServices.map((sp: any) => {
                        let freq = sp.frequency;
                        if (freq === 'seasonal-contract' && sp.seasonalContractDetails?.serviceFrequency) {
                          freq = sp.seasonalContractDetails.serviceFrequency;
                        }
                        return freq === 'per-instance' ? 'Per Instance' :
                               freq === 'weekly' ? 'Weekly' :
                               freq === 'bi-weekly' ? 'Bi-Weekly' :
                               freq === 'monthly' ? 'Monthly' :
                               freq === 'seasonal-contract' ? 'Seasonal Contract' :
                               freq;
                      }).join(', ')}
                    </Typography>
                    {seasonalService && contractStartDate && contractEndDate && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        <strong>Contract Period:</strong> {formatContractDateUTC(contractStartDate)} - {formatContractDateUTC(contractEndDate)}
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            </Box>
          )}

          {(() => {
            const isPerInstance = scheduleDialog.job ? isPerInstanceJob(scheduleDialog.job) : false;
            return (
              <>
                <TextField
                  fullWidth
                  label="First Service Date"
                  name="scheduledDate"
                  type="date"
                  value={scheduleForm.scheduledDate}
                  onChange={handleScheduleFormChange}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  required={!isPerInstance}
                  helperText={isPerInstance ? 'Optional: Per-instance services can be scheduled as needed' : 'Select the date for the first service. This date will determine the recurring schedule:'}
                />
                <TextField
                  fullWidth
                  label="Estimated Duration (hours)"
                  name="estimatedDuration"
                  type="number"
                  value={scheduleForm.estimatedDuration}
                  onChange={handleScheduleFormChange}
                  margin="normal"
                  inputProps={{ min: 0.25, step: 0.25, max: 8 }}
                  helperText="15 min increments (0.25, 0.5, 0.75, 1, …)"
                  required
                />
              </>
            );
          })()}
          {scheduleDialog.job?.acceptedBid?.servicePricing && scheduleForm.scheduledDate && (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
              {scheduleDialog.job.acceptedBid.servicePricing.map((sp: any, idx: number) => {
                const parseDateFromInput = (dateStr: string) => {
                  const [year, month, day] = dateStr.split('-').map(Number);
                  if (!year || !month || !day) return null;
                  // Create date in user's local timezone (no UTC shift)
                  return new Date(year, month - 1, day);
                };
                
                const date = parseDateFromInput(scheduleForm.scheduledDate);
                const dayOfWeek = date
                  ? date.toLocaleDateString('en-US', { weekday: 'long' })
                  : '';
                const dayOfMonth = date ? date.getDate() : 1;
                let scheduleText = '';
                let paymentNote = '';
                
                // Check for seasonal contract
                if (sp.frequency === 'seasonal-contract' || sp.isSeasonalContract) {
                  scheduleText = `Seasonal contract service`;
                  
                  // Determine payment schedule for seasonal contracts
                  let paymentSchedule: 'single-payment' | 'monthly' = 'single-payment';
                  
                  // Check acceptedBid.servicePricing (for bid-accepted customers)
                  if (sp.seasonalContractDetails?.paymentSchedule) {
                    paymentSchedule = sp.seasonalContractDetails.paymentSchedule;
                  }
                  
                  // Check customer.servicePricing (for onboarded customers)
                  if (scheduleDialog.job.customerId && typeof scheduleDialog.job.customerId === 'object') {
                    const customer = scheduleDialog.job.customerId as any;
                    const serviceCategory = scheduleDialog.job.serviceType?.category;
                    
                    if (customer.servicePricing) {
                      let customerPricingData: any = null;
                      if (serviceCategory === 'mowing' && customer.servicePricing.lawnMowingTrimming) {
                        customerPricingData = customer.servicePricing.lawnMowingTrimming;
                      } else if (serviceCategory === 'fertilizing' && customer.servicePricing.weedControlFertilizing) {
                        customerPricingData = customer.servicePricing.weedControlFertilizing;
                      } else if (serviceCategory === 'snow_removal' && customer.servicePricing.snowRemoval) {
                        customerPricingData = customer.servicePricing.snowRemoval;
                      }
                      
                      if (customerPricingData?.seasonalContractDetails?.paymentSchedule) {
                        paymentSchedule = customerPricingData.seasonalContractDetails.paymentSchedule;
                      }
                    }
                  }
                  
                  // Set payment note based on actual payment schedule
                  if (paymentSchedule === 'monthly') {
                    paymentNote = ' (Monthly payments - charged on the 1st of each month)';
                  } else {
                    paymentNote = ' (Full payment processed after first service)';
                  }
                } else if (sp.frequency === 'per-instance') {
                  scheduleText = 'Per-instance service (will charge when completed)';
                } else if (date && (sp.frequency === 'weekly' || sp.frequency === 'bi-weekly')) {
                  scheduleText = `Will recur every ${sp.frequency === 'weekly' ? '' : 'other '}${dayOfWeek}`;
                } else if (date && sp.frequency === 'monthly') {
                  scheduleText = `Will recur on the ${dayOfMonth}${getOrdinalSuffix(dayOfMonth)} of each month`;
                } else if (sp.frequency === 'seasonal-contract') {
                  scheduleText = 'Seasonal contract service';
                }
                return (
                  <Typography key={idx} variant="body2">
                    <strong>{sp.serviceName}:</strong> {scheduleText}{paymentNote}
                  </Typography>
                );
              })}
            </Alert>
          )}
          {scheduleError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {scheduleError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setScheduleDialog({ open: false, job: null });
              setScheduleForm({ scheduledDate: '', estimatedDuration: '' });
              setScheduleError(null);
            }}
            disabled={scheduleLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={submitSchedule} 
            variant="contained"
            disabled={scheduleLoading || !scheduleForm.scheduledDate || !scheduleForm.estimatedDuration}
          >
            {scheduleLoading ? 'Scheduling...' : 'Schedule Job'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Job Dialog */}
      <Dialog open={completeDialog.open} onClose={() => setCompleteDialog({ open: false, job: null, completionNotes: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Job</DialogTitle>
        <DialogContent>
          {completeDialog.job && (() => {
            // Check if this is a monthly payment seasonal contract
            const isMonthlyPaymentContract = (() => {
              const job = completeDialog.job;
              const serviceCategory = job.serviceType?.category;
              
              // Check acceptedBid (for bid-accepted customers)
              if (job.acceptedBid?.servicePricing) {
                const matchingServicePricing = job.acceptedBid.servicePricing.find((sp: any) => {
                  if (serviceCategory === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                  if (serviceCategory === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                  if (serviceCategory === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                  return false;
                });
                
                if (matchingServicePricing?.seasonalContractDetails?.paymentSchedule === 'monthly') {
                  return true;
                }
              }
              
              // Check customer.servicePricing (for onboarded customers)
              if (job.customerId && typeof job.customerId === 'object') {
                const customer = job.customerId as any;
                if (customer.servicePricing) {
                  let customerPricingData: any = null;
                  if (serviceCategory === 'mowing' && customer.servicePricing.lawnMowingTrimming) {
                    customerPricingData = customer.servicePricing.lawnMowingTrimming;
                  } else if (serviceCategory === 'fertilizing' && customer.servicePricing.weedControlFertilizing) {
                    customerPricingData = customer.servicePricing.weedControlFertilizing;
                  } else if (serviceCategory === 'snow_removal' && customer.servicePricing.snowRemoval) {
                    customerPricingData = customer.servicePricing.snowRemoval;
                  }
                  
                  if (customerPricingData?.seasonalContractDetails?.paymentSchedule === 'monthly') {
                    return true;
                  }
                }
              }
              
              return false;
            })();
            
            return (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Service: {formatServiceTypeName(completeDialog.job.serviceType?.name)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customer: {completeDialog.job.customerId?.firstName} {completeDialog.job.customerId?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Address: {completeDialog.job.address?.street}, {completeDialog.job.address?.city}
                </Typography>
                {isMonthlyPaymentContract ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Monthly Payment Seasonal Contract
                    </Typography>
                    <Typography variant="body2">
                      This customer has a monthly payment plan. Payments are handled automatically by Stripe subscription. No payment will be processed when completing this job.
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Completing this job will automatically process payment to your account.
                  </Alert>
                )}
              </Box>
            );
          })()}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Completion Notes (optional)"
            value={completeDialog.completionNotes}
            onChange={(e) => setCompleteDialog(prev => ({ ...prev, completionNotes: e.target.value }))}
            margin="normal"
            placeholder="Any notes about the completed work..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialog({ open: false, job: null, completionNotes: '' })}>
            Cancel
          </Button>
          <Button onClick={submitCompletion} variant="contained" color="success">
            Mark as Complete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Schedule Dialog */}
      <Dialog open={editScheduleDialog.open} onClose={() => {
        setEditScheduleDialog({ open: false, job: null, newDate: '', isDayChange: false });
        setScheduleUpdateError(null); // Clear errors when dialog closes
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Schedule</DialogTitle>
        <DialogContent>
          {editScheduleDialog.job && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Service: {formatServiceTypeName(editScheduleDialog.job.serviceType?.name)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customer: {editScheduleDialog.job.customerId?.firstName} {editScheduleDialog.job.customerId?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Address: {editScheduleDialog.job.address?.street}, {editScheduleDialog.job.address?.city}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Date: {editScheduleDialog.job.scheduledDate ? formatDateForDisplay(editScheduleDialog.job.scheduledDate) : 'Not scheduled'}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="New Service Date"
            name="newDate"
            type="date"
            value={editScheduleDialog.newDate}
            onChange={(e) => {
              setEditScheduleDialog(prev => ({ ...prev, newDate: e.target.value }));
            }}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {editScheduleDialog.job?.frequency === 'per-instance' 
              ? 'Per-instance service - this will update the scheduled date for this service only.'
              : 'Changing the day of the week will ask if you want to reset the recurring schedule.'}
          </Typography>
          {scheduleUpdateSuccess && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setScheduleUpdateSuccess(null)}>
              {scheduleUpdateSuccess}
            </Alert>
          )}
          {scheduleUpdateError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setScheduleUpdateError(null)}>
              {scheduleUpdateError}
            </Alert>
          )}
          {scheduleUpdateLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            type="button"
            onClick={() => setEditScheduleDialog({ open: false, job: null, newDate: '', isDayChange: false })}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              submitScheduleEdit();
            }} 
            variant="contained"
            disabled={scheduleUpdateLoading || !editScheduleDialog.newDate}
          >
            {scheduleUpdateLoading ? 'Updating...' : 'Update Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Day Reset Confirmation Dialog */}
      <Dialog 
        open={dayResetDialog.open} 
        onClose={() => setDayResetDialog({ open: false, job: null, newDate: '', resetSchedule: false })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Change Type</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            How would you like to update the service date?
          </Typography>
          {scheduleUpdateError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleUpdateError(null)}>
              {scheduleUpdateError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              type="button"
              variant="outlined"
              fullWidth
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (dayResetDialog.job) {
                  try {
                    console.log('[DAY-RESET-DIALOG] One-time change clicked');
                    await updateScheduleDate(dayResetDialog.job, dayResetDialog.newDate, false);
                    console.log('[DAY-RESET-DIALOG] One-time change completed');
                    // Dialog will be closed by updateScheduleDate on success
                  } catch (err: any) {
                    console.error('[DAY-RESET-DIALOG] Error updating schedule (one-time):', err);
                    // Error is already handled in updateScheduleDate - dialog stays open so user can retry
                  }
                }
              }}
              disabled={scheduleUpdateLoading || scheduleUpdateSuccess !== null}
              sx={{ p: 2, textAlign: 'left', justifyContent: 'flex-start' }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  One-time Change
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Only change this specific service date. The recurring schedule will remain unchanged.
                </Typography>
              </Box>
            </Button>
            <Button
              type="button"
              variant="contained"
              fullWidth
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (dayResetDialog.job) {
                  try {
                    console.log('[DAY-RESET-DIALOG] Permanent change clicked');
                    await updateScheduleDate(dayResetDialog.job, dayResetDialog.newDate, true);
                    console.log('[DAY-RESET-DIALOG] Permanent change completed');
                    // Dialog will be closed by updateScheduleDate on success
                  } catch (err: any) {
                    console.error('[DAY-RESET-DIALOG] Error updating schedule (permanent):', err);
                    // Error is already handled in updateScheduleDate - dialog stays open so user can retry
                  }
                }
              }}
              disabled={scheduleUpdateLoading || scheduleUpdateSuccess !== null}
              sx={{ 
                p: 2, 
                textAlign: 'left', 
                justifyContent: 'flex-start',
                color: 'white',
                '& .MuiTypography-root': {
                  color: 'white !important'
                }
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Permanent Change
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Update the recurring schedule. All future service dates will be based on this new date.
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayResetDialog({ open: false, job: null, newDate: '', resetSchedule: false })}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProviderDashboard;
